import {
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { setOrderXeroAddress } from "../hooks/useUserAddresses";
import { setOrderXeroBrandingTheme } from "../hooks/useXeroBrandingThemes";
import constate from "constate";
import { UserContext, EntitlementsContext } from "../contexts";
import {
  useAllFx,
  useFx,
  tableRate as lookupRate,
  needsMissingRate as needs,
} from "../hooks/fx";
import {
  getBaseCurrency,
  isMulticurrency,
  orderItemCurrency,
  toPence,
} from "../core/currency";
import { getListing } from "../listings/services/listing";
import { useAlert } from "react-alert";
import { useApiError } from "../hooks/useApiError.js";
import {
  MAX_LISTINGS,
  MAX_SELL_REQUESTS,
  STEPS,
  PAYMENT_DIALOG,
} from "./constants";
import {
  useAddToCart,
  useRegisterDeposits,
  useRegisterPxOrder,
  useRegisterPaidOrder,
} from '../cart/index.jsx';
import { useRegisterCommissions } from "../cart/orderManagementApi";
import { invoiceNotUpdatedMessage } from "./helpers/invoiceMessages";
import { requireResponse } from "../core/errors/requireResponse";
import { usePoll } from "../hooks/usePoll";
import { isEmpty } from "lodash";

// How often to ask whether an outstanding STRIPE LINK has been paid, while
// the rep has the order open and the tab visible. 10s is the same order of
// magnitude as the POS poll (PaymentRow) and costs one cheap query per tick
// against an endpoint the page already calls.
const STRIPE_LINK_POLL_MS = 10000;

// How long to wait after a link settles before re-reading the invoice PDF.
// The payment reaches Xero via a queued job (RecordXeroPaymentJob), so an
// immediate read would fetch the pre-payment document.
const INVOICE_SETTLE_REFRESH_MS = 6000;

// Fees on these rows are FX / handling / network fees, never a card fee —
// they total under "FX / OTHER FEE", every other method under "CARD FEE".
const isNonCardFeeMethod = (method) =>
  method === "offline" || method === "cash" || method === "usdt";

function validateAssignedListing(listing) {
  if (!listing.price) {
    return "The listing must have a price.";
  }

  if (listing.status !== "active" || listing.is_reserved) {
    return "The listing must be active and not reserved for a client.";
  }

  return null;
}

// lookupRate (hooks/fx tableRate) reads the row for `from`, or its peg's row
// for USDT. A missing rate still multiplies by 1 HERE so the maths never produces NaN,
// but every such case is caught by `fxMissing` in the provider: the figures
// are then hidden by the consumers and the saves refuse. See FX RULE in
// hooks/fx.js.
function convertAmount(rates, amount, from = "gbp") {
  const convertedAmount = amount * (lookupRate(rates, from) || 1);

  return convertedAmount;
}

async function loadPxRequest(pxRequestId, callback) {
  const { data } = await axios.get(`/api/px-requests/${pxRequestId}`);

  callback(data.sell_requests, data.selectedQuotes);
}

async function loadQuote(quoteId, callback) {
  const { data } = await axios.get(`/api/quotes/${quoteId}`);

  callback(data);
}

const [OrderManagementProvider, useOrderManagement] = constate(
  ({
    client,
    editingOrder,
    closeEditingOrder,
    mode = "modal",
    initialListings,
    initialStep,
    initialSellRequests,
    initialIsPxOrder,
    openAssignSellRequestsKey,
    // V2 inline mode — the parent Order Request id. Used when admin
    // clicks PROCEED on ASSIGN SELL REQUESTS for a request that
    // hasn't materialised into a cart Order yet: we POST to
    // /api/px-requests with request_id so the backend can attach the
    // PxRequest to the Order Request directly.
    parentRequestId,
    // V2 inline mode — persisted quote selections from the PxRequest
    // (useRequestContext.quotes set by loadProducts from
    // data.selectedQuotes). Seeds om.quotes so reload-restored
    // checkmarks are visible. Same timing pattern as
    // initialSellRequests: the request load is async, so a useEffect
    // syncs once data arrives.
    initialQuotes,
    // Callback to refresh the parent request after PX link/dissolve.
    // V2 wires this to useRequest.refreshRequest so the page picks up
    // request.px_request_id changes immediately (without needing
    // refresh) and Design 2's loadProducts overlay pulls the fresh
    // sell_requests / pxProducts.
    onRequestRefresh,
    // Preferred user for the Split Commission slot-1 default. V2 wires
    // this to request.created_by; we promote them only when they're an
    // is_sales_admin, otherwise fall back to the currently logged-in
    // admin per Alex's rule. Default only applies on first seed — once
    // the rep saves any slot, persisted state wins on subsequent loads.
    defaultCommissionUser,
  }) => {
    const isInline = mode === "inline";
    const { user: loggedInUser } = useContext(UserContext) || {};
    const { addToCart } = useAddToCart();
    const { registerPxOrder } = useRegisterPxOrder(); // unpaid PX order
    const { registerDeposits } = useRegisterDeposits(); // deposits for PX or normal order
    const { registerPaidOrder } = useRegisterPaidOrder(); // paid PX or normal order
    const { registerCommissions } = useRegisterCommissions();

    const seededStep = isInline ? -1 : STEPS.SELECT_LISTINGS;
    const [step, setStep] = useState(seededStep);
    const [lastStepInternal, setLastStepInternal] = useState(
      isInline ? STEPS.TRACKING_NUMBER : seededStep
    );
    // In inline mode lastStep is pinned at TRACKING_NUMBER so every cart
    // section stays mounted (each molecule's `if (lastStep < STEPS.X)`
    // early-return otherwise shrinks the rendered set when navigation
    // functions like goToCommissions / goToInvoice / skipSellRequests
    // try to lower it.
    const lastStep = lastStepInternal;
    const setLastStep = (val) => {
      if (isInline) return;
      setLastStepInternal(val);
    };
    const [flowModalOpen, setFlowModalOpen] = useState(
      isInline || !!editingOrder
    );
    const [isPxOrder, setIsPxOrder] = useState(
      isInline
        ? initialIsPxOrder ?? (initialSellRequests?.length || 0) > 0
        : null
    );
    const pxLinkedSellRequests = isInline ? initialSellRequests ?? [] : [];
    const [
      assignSellRequestsOpen,
      setAssignSellRequestsOpen,
    ] = useState(false);
    const [
      sellRequestsSkipped,
      setSellRequestsSkipped,
    ] = useState(false);
    useEffect(() => {
      if (!isInline) return;
      if (openAssignSellRequestsKey === undefined) return;
      setAssignSellRequestsOpen(true);
      setSellRequestsSkipped(false);
    }, [openAssignSellRequestsKey]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [getPdf, setGetPdf] = useState("");
    // Signed /api/pdfs URL for the same file as getPdf. The INVOICE viewer
    // renders it in a token-less <object data=…>, which 403s on a bare
    // /api/pdfs/{id} once the file is gated by the backend's FileAccessPolicy.
    // getPdf stays the numeric id (section gating + the send-invoice call).
    const [getPdfUrl, setGetPdfUrl] = useState("");
    const [paymentDialog, setPaymentDialog] = useState({
      open: false,
      step: null,
      decision: null,
    });
    const [showPaymentErrors, setShowPaymentErrors] = useState(false);

    const [order, setOrder] = useState(null);
    // A store without Xero invoicing on its plan gets our own invoice PDF
    // (backend NativeDocumentService): no Xero org to list, and no Xero bank
    // account a payment has to name. An order already invoiced in Xero stays.
    const { hasFeature } = useContext(EntitlementsContext);
    const hasXeroInvoicing = hasFeature("xero_invoicing");
    const nativeInvoice = !hasXeroInvoicing && !order?.xero_invoice_number;

    // ── Xero organisation selection (multi-org) ──────────────────────────────
    // Which connected Xero org this order's invoice/payments/contact go to. The
    // rep picks it in the PAYMENTS section before generating the invoice; once an
    // invoice exists the order keeps its pinned org (the dropdown locks).
    const [xeroOrganisations, setXeroOrganisations] = useState([]);
    const [xeroConnectionId, setXeroConnectionId] = useState(
      order?.xero_connection_id ?? null
    );

    // A switch awaiting confirmation in SwitchXeroOrgModal. Null = no dialog.
    // Holds everything the dialog needs to describe the change, plus what to
    // restore if it's cancelled or the switch fails. See onXeroOrgChange.
    const [xeroOrgSwitch, setXeroOrgSwitch] = useState(null);

    useEffect(() => {
      // The route is plan-gated: asking anyway only earns a "not in your plan"
      // toast every time PAYMENTS opens.
      if (!hasXeroInvoicing) return undefined;
      let active = true;
      axios
        .get("/api/xero/organisations")
        .then(({ data }) => {
          if (active) setXeroOrganisations(data?.connections || []);
        })
        .catch(() => {
          /* Xero orgs endpoint not deployed on this env — dropdown stays empty */
        });
      return () => {
        active = false;
      };
    }, [hasXeroInvoicing]);

    // Keep the selection in sync: a locked order keeps its pinned org; otherwise
    // default to the org flagged default once the list arrives.
    useEffect(() => {
      if (order?.xero_connection_id) {
        setXeroConnectionId(order.xero_connection_id);
      } else if (xeroConnectionId == null && xeroOrganisations.length) {
        const def =
          xeroOrganisations.find((o) => o.is_default) || xeroOrganisations[0];
        setXeroConnectionId(def?.id ?? null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.xero_connection_id, xeroOrganisations]);

    // Which of the client's saved addresses this invoice is billed to. Null =
    // the client's default address, i.e. the behaviour before the address book
    // existed. Unlike the org this stays changeable after an invoice exists —
    // correcting a wrong address is exactly why you would re-issue one.
    const [xeroAddressId, setXeroAddressId] = useState(
      order?.xero_address_id ?? null
    );

    useEffect(() => {
      setXeroAddressId(order?.xero_address_id ?? null);
    }, [order?.xero_address_id]);

    // Persisted immediately (like the org switch) so the choice survives a
    // reload and is on the order by the time "Go To Invoice" runs. Local state
    // updates first so the select never feels laggy.
    const onXeroAddressChange = useCallback(
      async (addressId) => {
        setXeroAddressId(addressId);
        if (!order?.id) return;
        try {
          await setOrderXeroAddress(order.id, addressId);
        } catch (e) {
          console.warn("Could not pin the invoice address", e);
        }
      },
      [order?.id]
    );

    // ── Branding theme (Xero letterhead) ─────────────────────────────────────
    // Which branding theme this order's invoice is generated with. Null = the
    // organisation's default theme, i.e. the behaviour before the picker
    // existed. Like the address, and unlike the org, it stays changeable after
    // an invoice exists — re-issuing under a corrected letterhead is the point.
    const [xeroBrandingThemeId, setXeroBrandingThemeId] = useState(
      order?.xero_branding_theme_id ?? null
    );

    useEffect(() => {
      setXeroBrandingThemeId(order?.xero_branding_theme_id ?? null);
    }, [order?.xero_branding_theme_id]);

    // A theme picked BEFORE the order exists (the cart flow only creates the
    // order on save) can't be persisted yet, so it is parked here and flushed
    // once there is an order to hang it on. Held in a ref rather than derived
    // from state so it can only ever describe a pick made with NO order —
    // nothing belonging to a previously loaded order can leak onto a new one.
    const pendingBrandingThemeRef = useRef(null);

    // Persisted immediately, like the org and address picks, so the choice
    // survives a reload and is on the order by the time "Go To Invoice" runs.
    const onXeroBrandingChange = useCallback(
      async (themeId) => {
        setXeroBrandingThemeId(themeId);
        if (!order?.id) {
          pendingBrandingThemeRef.current = themeId;
          return;
        }
        try {
          await setOrderXeroBrandingTheme(order.id, themeId);
        } catch (e) {
          console.warn("Could not pin the invoice branding theme", e);
        }
      },
      [order?.id]
    );

    useEffect(() => {
      const pending = pendingBrandingThemeRef.current;
      if (!order?.id || !pending) {
        return;
      }
      pendingBrandingThemeRef.current = null;
      setOrderXeroBrandingTheme(order.id, pending).catch((e) => {
        console.warn("Could not pin the invoice branding theme", e);
      });
    }, [order?.id]);

    // Default currency for NEW / unfilled payment rows (deposits + total
    // remaining). Product rule: the STORE BASE currency comes FIRST; the
    // selected Xero org's currency is only the fallback when no base is set
    // (multi-currency off), then GBP before an org resolves. Saved / filled
    // rows keep their stored currency — see the re-seed effect below.
    const baseCurrency = useMemo(() => {
      if (isMulticurrency()) {
        return getBaseCurrency();
      }
      const org = xeroOrganisations.find(
        (o) => Number(o.id) === Number(xeroConnectionId)
      );
      return (org?.currency || "gbp").toLowerCase();
    }, [xeroOrganisations, xeroConnectionId]);

    const [listings, setListings] = useState(
      isInline ? initialListings ?? [] : []
    );
    const [sellRequests, setSellRequests] = useState(
      isInline ? initialSellRequests ?? [] : []
    );

    // Re-seed sellRequests when initialSellRequests arrives after the
    // provider has already mounted. Common in V2: useRequest's async
    // loadProducts overlay fetches /api/px-requests/{id} AFTER first
    // render, so pxRequests transitions from [] → [Rolex]. Without
    // this effect the provider stays stuck at the empty mount-time
    // state, and PxSummary / CalcSummary show TOTAL SELL with no rows
    // even though the smart card (which reads from the freshly-loaded
    // pxRequests) shows the trade-in.
    useEffect(() => {
      if (!isInline) return;
      const next = initialSellRequests ?? [];
      if (next.length === 0) return;
      const nextIds = next.map((s) => s?.id).join(",");
      const currentIds = sellRequests.map((s) => s?.id).join(",");
      if (currentIds === nextIds) return;
      // Only sync if admin hasn't actively diverged from previous
      // initial — i.e. current state is empty OR matches no items.
      if (sellRequests.length === 0) {
        setSellRequests(next);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      (initialSellRequests ?? []).map((s) => s?.id).join(","),
    ]);

    // Same async-mount problem applies to listings, with a twist:
    // for a multi-main PX deal seededListings populates in MULTIPLE
    // waves — primary's fetchedListing arrives via POST
    // /api/quotes/{quoteId}/listing, then each additional slot's
    // listing arrives via its own GET /api/listings/{slotListingId}.
    // The sell-side re-sync only fires when `current.length === 0`
    // because sells arrive all-at-once in the px-request bundle.
    // Listings need a stricter test: re-sync whenever current is a
    // SUBSET of initial (i.e. new listings being appended), but
    // leave alone if admin has actively diverged (a listing in
    // current that isn't in initial).
    //
    // Without this, OM ends up with just the primary listing for a
    // 3-main deal — Payments renders only Listing 1, toPay collapses,
    // and PX SUMMARY shows wrong totals.
    useEffect(() => {
      if (!isInline) return;
      const next = initialListings ?? [];
      if (next.length === 0) return;
      const nextIds = next.map((l) => l?.id);
      const currentIds = listings.map((l) => l?.id);
      if (nextIds.join(",") === currentIds.join(",")) return;
      // Re-sync when current is empty OR current is a subset of next
      // (admin hasn't introduced a listing that isn't in initial).
      const isSubset =
        currentIds.length === 0 ||
        currentIds.every((id) => nextIds.includes(id));
      if (isSubset) {
        setListings(next);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      (initialListings ?? []).map((l) => l?.id).join(","),
    ]);
    const [quotes, setQuotes] = useState(
      isInline ? initialQuotes ?? {} : {}
    );

    // Mirror the initialSellRequests sync: when V2's async load brings
    // in selectedQuotes after the provider mounted, fold them into
    // om.quotes. Merge so that any key already present in om.quotes
    // (admin's active picks — typically the buy-side quote) wins, while
    // new keys (sell-side selectedQuotes that arrive late from
    // loadProducts) get seeded. The previous "only sync when om is
    // empty" gate skipped sync as soon as om had a single buy-side
    // selection, leaving sell-side quotes unhydrated and turning toPay
    // / Live Calc into the full listing price.
    useEffect(() => {
      if (!isInline) return;
      const next = initialQuotes ?? {};
      const nextIds = Object.keys(next)
        .map((k) => `${k}:${next[k]?.id ?? ""}`)
        .join(",");
      const currentIds = Object.keys(quotes)
        .map((k) => `${k}:${quotes[k]?.id ?? ""}`)
        .join(",");
      if (currentIds === nextIds) return;
      setQuotes((prev) => ({ ...next, ...prev }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      Object.keys(initialQuotes ?? {})
        .map((k) => `${k}:${initialQuotes[k]?.id ?? ""}`)
        .join(","),
    ]);
    // Inline mode seeds listings from initialListings but `editingOrder` is
    // absent, so the existing editingOrder useEffect (~line 200) doesn't run
    // and `deposits[listing.id]` stays undefined — Payments.jsx then renders
    // an empty "Deposit Paid:" block because `deposits[listing.id]?.map(...)`
    // has nothing to iterate. Match DAO's onSelectListing behaviour by
    // seeding one empty deposit row per inline listing.
    const seedDepositsForListings = (ls) =>
      (ls ?? []).reduce((acc, l) => {
        acc[l.id] = [
          {
            payment_status: "created",
            amount: 0,
            currency: baseCurrency,
            method: "offline",
            key: Math.random(),
          },
        ];
        return acc;
      }, {});
    const [deposits, setDeposits] = useState(
      isInline ? seedDepositsForListings(initialListings) : {}
    );
    const [taxRates, setTaxRates] = useState({
      listings: isInline
        ? (initialListings ?? []).reduce((acc, l) => {
            acc[l.id] = l.tax_rate || "margin_scheme";
            return acc;
          }, {})
        : {},
      requests: {},
    });
    const [totalRemainingPaid, setTotalRemainingPaid] = useState({
      amount: 0,
      currency: baseCurrency,
      method: "offline",
    });
    const [commissions, setCommissions] = useState({});
    // EXPERT FEES — one optional row per order, shaped like totalRemainingPaid.
    // `expert_account_id` replaces `bank_account_id`: an expert is a name the
    // merchant maintains, not a Xero bank account, and this row never reaches
    // an invoice. The amount is REVENUE the client paid on top of the watch, so
    // it raises the deal's profit and the commission pool — it only counts once
    // BOTH an amount and an expert are set, a rule that lives in expertFeeGbp.
    const [expertFee, setExpertFee] = useState({
      amount: 0,
      currency: baseCurrency,
      method: "offline",
      expert_account_id: null,
    });
    const [marketplaceFee, setMarketplaceFee] = useState(0);
    const [marketplaceFeeType, setMarketplaceFeeType] = useState(null);
    const [trackingNumber, setTrackingNumber] = useState(null);

    const [costsCurrentListingIdx, setCostsCurrentListingIdx] = useState(0);

    // When the chosen Xero org changes, refresh the default currency on payment
    // rows the rep hasn't filled yet (amount 0) so new/empty lines follow the
    // org's base currency. Rows with an entered amount — and everything already
    // saved — keep their own currency, so historical data is never re-denominated.
    //
    // PRODUCT RULE: the store-base-first default applies to EMPTY, UNTOUCHED
    // rows only. The moment an admin picks a currency on the select (or a row
    // hydrates with a currency the admin previously saved) that choice is
    // ADMIN-OWNED and this effect must never rewrite it — `currency_touched`
    // is the marker. `amount` alone was not a sufficient guard: `baseCurrency`
    // re-resolves asynchronously (the Xero-org fetch lands ~after mount, and
    // getBaseCurrency() only becomes non-'gbp' once SettingsContext seeds), so
    // a currency picked BEFORE the amount was typed was silently reverted to
    // the default a fraction of a second later. `currency_touched` is a local
    // UI flag only — prepareOrderDataWithPayments maps named fields, so it is
    // never sent to the API.
    const currencyIsAdminOwned = (row) =>
      !!row?.currency_touched || !!parseFloat(row?.amount || 0);
    useEffect(() => {
      setDeposits((prev) => {
        let changed = false;
        const next = {};
        Object.entries(prev || {}).forEach(([lid, rows]) => {
          next[lid] = (rows || []).map((r) => {
            if (!currencyIsAdminOwned(r) && r.currency !== baseCurrency) {
              changed = true;
              return { ...r, currency: baseCurrency };
            }
            return r;
          });
        });
        return changed ? next : prev;
      });
      setTotalRemainingPaid((trp) =>
        !currencyIsAdminOwned(trp) && trp.currency !== baseCurrency
          ? { ...trp, currency: baseCurrency }
          : trp
      );
      setExpertFee((ef) =>
        !currencyIsAdminOwned(ef) && ef.currency !== baseCurrency
          ? { ...ef, currency: baseCurrency }
          : ef
      );
    }, [baseCurrency]);

    // Order currency follows the QUOTE, not the listing. Listings created from
    // a quote carry the raw quote amount but no currency, so `listing.currency`
    // is null/gbp and defaulted the whole order to GBP. Match each listing to
    // its quote via `listing_id` (quote -> listing -> gbp), and use the first
    // resolved currency as the FX base. Mixed-currency carts are rejected on
    // save by the backend; here we just take the first for display/FX.
    const resolveListingCurrency = (listing) => {
      const q = Object.values(quotes || {}).find(
        (qt) => qt?.listing_id === listing?.id
      );
      // quote → listing's DEAL currency → store base ('gbp' until
      // multi-currency is seeded). `deal_currency` is the quote-derived
      // currency RequestTemplateV2 stamps alongside (never over) the listing's
      // own `currency`, which is the currency of listing.price itself — legacy
      // GBP when the listing has none. Reading `currency` here would resolve a
      // €26,850-quoted deal to GBP (order 87362).
      return (
        q?.currency ||
        listing?.deal_currency ||
        listing?.currency ||
        getBaseCurrency()
      ).toLowerCase();
    };
    /**
     * The currency `listing.price` is actually denominated in — the listing's
     * OWN sell-price currency (`listings.currency`, migration
     * 2026_07_21_150001), with NULL/absent meaning LEGACY GBP.
     *
     * It is NOT the deal currency, and it must never fall back to it: order
     * 87362 carries a £23,500 listing sold on a €26,850 quote, and pairing that
     * amount with the deal's currency is exactly what rendered "€23,500".
     *
     * Use it to LABEL a listing's own price (the Payments listing heading), the
     * same rule the Order Summary card's cart line applies, so the two surfaces
     * can never disagree about one listing's price. Do NOT use it to convert
     * `toPay`: that total mirrors order.total_amount, which the backend builds
     * from raw listing prices (see the comment in `toPay`).
     *
     * Since BE #1978 QuoteService::createListing/updateListing stamp
     * `'currency' => $quote->currency` alongside the copied price, so a
     * quote-derived listing carries the deal's currency on its own record and
     * the two agree by construction. A NULL currency is now only ever a
     * pre-2026-07 row, i.e. genuinely legacy GBP.
     */
    const listingPriceCurrency = (listing) =>
      (listing?.currency || "gbp").toLowerCase();
    /** Same, but NULL-preserving, so it can sit in a fallback chain. */
    const ownListingCurrency = (listing) =>
      listing?.currency ? String(listing.currency).toLowerCase() : null;
    // ── The currency this order is denominated in ─────────────────────────────
    // `toPay` is Σ listing.price with NO conversion, and every paid/fee total
    // below is converted INTO this code, so it must be the currency those
    // listing prices are actually in — the SAME-RECORD rule the Order Summary
    // card applies to every line it renders. Read it off the listings first.
    //
    // It used to read `editingOrder.currency` first, back when an order's
    // listings were seeded from order.items[].listing and `listings` had no
    // currency column, so the listings could only ever answer "gbp". They carry
    // one now (migration 2026_07_21_150001), and preferring the stored order
    // code became actively wrong: `orders.currency` was added with DEFAULT
    // 'gbp' (migration 2026_06_25_120000) and is only ever re-resolved when an
    // order's total is recomputed, so EVERY order created before that migration
    // holds a default 'gbp' nobody chose. On production order 87362 that
    // printed a €26,850 order as "£26,850" in TOTAL LISTING PRICE while the
    // LISTING 1 heading two rows above it — same number, same record, read
    // through listingPriceCurrency — correctly said "26,850.00€"; it then
    // fed a phantom "Due: £3,873.65" (26,850 − the same money converted to
    // 22,976) and inflated Net Profit by treating €26,850 as £26,850.
    //
    // Order of resolution: the listings' own price currency → the stored order
    // code → the quote-first deal resolution (pre-order cart, before a listing
    // exists) → the store base. A legacy listing with NULL currency still falls
    // through to exactly what it resolved to before, so all-GBP orders are
    // unchanged.
    const listingRate = (
      (listings || []).map(ownListingCurrency).find(Boolean) ||
      editingOrder?.currency ||
      (listings || []).map(resolveListingCurrency)[0] ||
      getBaseCurrency()
    ).toLowerCase();
    const { rates, status: ratesStatus } = useAllFx(listingRate);
    // Rate to convert a selling amount (denominated in the order currency)
    // INTO GBP, for profit maths against the GBP-denominated costs
    // (gbp_buying_price etc.). Returns 1 for a GBP order.
    const { rate: sellingToGbpRate, unavailable: sellingToGbpUnavailable } =
      useFx(listingRate, "gbp");
    // Rate to convert a GBP-pivot amount (internal costs / commissions / net
    // profit) INTO the merchant's base currency for display. 1 for a GBP base.
    const { rate: gbpToBaseRate } = useFx("gbp", getBaseCurrency());
    // Rates INTO GBP, keyed by the SOURCE currency (useAllFx('gbp')). Used to
    // express a GBP-native amount — the commission share, derived from the GBP
    // itemProfit — back in a slot's chosen payout currency: gbp→ccy is the
    // reciprocal of the ccy→gbp key. Unseeded / flag-off ⇒ {} ⇒ ÷1.
    const { rates: gbpRates, status: gbpRatesStatus } = useAllFx("gbp");
    const gbpToCurrency = (amountGbp, ccy) =>
      amountGbp / (lookupRate(gbpRates, String(ccy || "gbp")) || 1);
    const alert = useAlert();
    const { showApiError } = useApiError();

    useEffect(() => {
      if (flowModalOpen || isInline) {
        return;
      }

      setStep(STEPS.SELECT_LISTINGS);
      setLastStep(STEPS.SELECT_LISTINGS);
      setIsPxOrder(null);
      setError(null);
      setSubmitting(false);
      setPaymentDialog({
        open: false,
        step: null,
        decision: null,
      });
      setShowPaymentErrors(false);
      setOrder(null);
      setListings([]);
      setGetPdf("");
      setGetPdfUrl("");
      setSellRequests([]);
      setQuotes({});
      setDeposits({});
      setTaxRates({
        listings: {},
        requests: {},
      });
      setTotalRemainingPaid({
        amount: 0,
        currency: baseCurrency,
        method: "offline",
      });
      setCommissions({});
      setExpertFee({
        amount: 0,
        currency: baseCurrency,
        method: "offline",
        expert_account_id: null,
      });
      setMarketplaceFee(0);
      setMarketplaceFeeType(null);
      setTrackingNumber(null);
      setCostsCurrentListingIdx(0);
    }, [flowModalOpen]);

    useEffect(() => {
      if (editingOrder) {
        if (!isInline) {
          setStep(STEPS.TRACKING_NUMBER);
          setLastStep(STEPS.TRACKING_NUMBER);
        }
        setIsPxOrder(!!editingOrder.px_request_id);

        setOrder(editingOrder);
        initCommisions(editingOrder);
        setListings(editingOrder?.items?.map((item) => item.listing) || []);
        setDeposits(
          editingOrder?.items?.reduce((acc, item) => {
            // `/api/order-management/register-deposits` returns Order
            // items WITHOUT the `deposits` relation eager-loaded — so
            // immediately after creating a deposit, items[].deposits
            // is undefined. Treat as empty so hydration doesn't throw
            // and the in-memory user input still shows until the next
            // full refresh (which goes through /api/requests/:id).
            acc[item.listing_id] = (item.deposits ?? []).map((deposit) => {
              const linkId = deposit.payment?.payment_link_id ?? null;
              const linkStatus = deposit.payment?.payment_link_status ?? null;
              // Two signals that a row is "stripe_link" in the UI:
              //   1. A PaymentLink is attached (post-Generate state).
              //   2. The Payment was saved with our bank_account_id="stripe"
              //      placeholder (pre-Generate state — Save Deposits was
              //      clicked but the link hasn't been generated yet).
              // Without (2), reopening an unsaved stripe_link row would
              // collapse back to plain stripe_card and the Generate button
              // would disappear.
              // Sentinel used pre-Generate to round-trip stripe_link intent
              // through the backend (which validates bank_account_id as
              // `required|string` and method as `card|stripe_card|offline`).
              // Accept both the current "__stripe_link__" token and the
              // legacy plain "stripe" token so rows saved before the
              // rename still hydrate correctly. Long-term fix is a backend
              // validation change to allow nullable bank_account_id for
              // stripe_card, then we drop the sentinel entirely.
              const isStripeLinkPlaceholder =
                deposit.payment?.method === "stripe_card" &&
                (deposit.payment?.bank_account_id === "__stripe_link__" ||
                  deposit.payment?.bank_account_id === "stripe");
              const uiMethod = linkId || isStripeLinkPlaceholder
                ? "stripe_link"
                : deposit.payment?.method;
              return {
                payment_id: deposit.payment_id,
                payment_status: deposit.payment?.status,
                amount: deposit.amount / 100,
                currency: deposit.currency?.toLowerCase(),
                // A currency that came back FROM the server is one an admin
                // already chose and saved, so the store-base default must not
                // re-seed it (see currencyIsAdminOwned). Rows stored without a
                // currency stay unmarked and are read as legacy GBP downstream.
                currency_touched: !!deposit.currency,
                date: deposit.payment?.date || deposit?.created_at,
                method: uiMethod,
                card_type: deposit.payment?.card_type,
                fee: (deposit.payment?.fee || 0) / 100,
                bank_account_id: deposit.payment.bank_account_id,
                // STRIPE LINK row context — used by PaymentRow to render
                // the Generate button + status pill and by the modal to
                // bind the link to a specific OrderItem.
                order_item_id: item.id,
                payment_link_id: linkId,
                payment_link_status: linkStatus,
                key: deposit.id,
              };
            });

            if (acc[item.listing_id].length < 3) {
              acc[item.listing_id].push({
                payment_status: "created",
                amount: 0,
                currency: baseCurrency,
                method: "offline",
                order_item_id: item.id,
                payment_link_id: null,
                payment_link_status: null,
                key: Math.random(),
              });
            }

            return acc;
          }, {})
        );
        setTotalRemainingPaid(
          editingOrder.payment
            ? (() => {
                // Mirror the deposit hydration: a TRP Payment saved as a
                // STRIPE LINK draft sits at method=stripe_card with the
                // __stripe_link__ sentinel in bank_account_id (Path A).
                // Recognise either the sentinel or an attached payment_link_id
                // and surface it as stripe_link in the UI; otherwise pass the
                // backend method through unchanged.
                const linkId = editingOrder.payment?.payment_link_id ?? null;
                const isStripeLinkPlaceholder =
                  editingOrder.payment?.method === "stripe_card" &&
                  (editingOrder.payment?.bank_account_id === "__stripe_link__" ||
                    editingOrder.payment?.bank_account_id === "stripe");
                const uiMethod = linkId || isStripeLinkPlaceholder
                  ? "stripe_link"
                  : editingOrder.payment?.method;
                return {
                  payment_id: editingOrder.payment_id,
                  payment_status: editingOrder.payment?.status,
                  amount: editingOrder.payment.amount / 100,
                  currency: editingOrder.payment?.currency?.toLowerCase(),
                  // Same rule as the deposit rows above: a saved currency is
                  // the admin's own choice and outranks the base default.
                  currency_touched: !!editingOrder.payment?.currency,
                  date:
                    editingOrder.payment?.date ||
                    editingOrder?.payment?.created_at,
                  method: uiMethod,
                  card_type: editingOrder.payment?.card_type,
                  bank_account_id: editingOrder.payment?.bank_account_id,
                  fee: (editingOrder.payment?.fee || 0) / 100,
                  payment_link_id: linkId,
                  payment_link_status:
                    editingOrder.payment?.payment_link_status ?? null,
                  key: editingOrder.payment.id,
                };
              })()
            : {
                amount: 0,
                currency: baseCurrency,
                method: "offline",
              }
        );
        // EXPERT FEES. The relation serialises as `expert_fee` (snake_case of
        // Order::expertFee) and rides in Order's $with, so it is present on the
        // same payload the TRP row hydrates from. A missing row is the common
        // case — the fee is optional — and resets to an empty row rather than
        // leaving the previous order's values behind.
        setExpertFee(
          editingOrder.expert_fee
            ? {
                id: editingOrder.expert_fee.id,
                expert_account_id: editingOrder.expert_fee.expert_account_id,
                // ExpertFee eager-loads `expertAccount`, so the name arrives
                // with the order — the card can name the expert on first paint.
                expert_account_name:
                  editingOrder.expert_fee.expert_account?.name ?? null,
                payment_id: editingOrder.expert_fee.payment_id,
                payment_status: editingOrder.expert_fee.payment?.status,
                amount: (editingOrder.expert_fee.payment?.amount || 0) / 100,
                currency:
                  editingOrder.expert_fee.payment?.currency?.toLowerCase() ||
                  baseCurrency,
                // Same rule as the deposit / TRP rows: a currency that came
                // back from the server is the admin's own choice and outranks
                // the store-base default.
                currency_touched: !!editingOrder.expert_fee.payment?.currency,
                date: editingOrder.expert_fee.payment?.date || undefined,
                method: editingOrder.expert_fee.payment?.method || "offline",
                card_type: editingOrder.expert_fee.payment?.card_type,
                // No `fee`: this row has no transaction-fee field at all.
              }
            : {
                amount: 0,
                currency: baseCurrency,
                method: "offline",
                expert_account_id: null,
              }
        );
        setTaxRates({
          listings: editingOrder.items?.reduce((acc, item) => {
            acc[item.listing_id] = item.listing?.tax_rate || "margin_scheme";
            return acc;
          }, {}),
        });
        setCostsCurrentListingIdx((editingOrder?.items?.length || 1) - 1);
        setMarketplaceFee((editingOrder.marketplace_fee || 0) / 100);
        setMarketplaceFeeType(editingOrder.marketplace_fee_type);
        setTrackingNumber(editingOrder.tracking_number);

        if (editingOrder.px_request_id) {
          setSubmitting(true);

          loadPxRequest(
            editingOrder.px_request_id,
            (sellRequests, selectedQuotes) => {
              setSellRequests(sellRequests);

              sellRequests?.forEach((sr) => {
                setTaxRates((taxRates) => ({
                  ...taxRates,
                  requests: {
                    ...taxRates.requests,
                    [sr.id]: sr.listing?.tax_rate || "margin_scheme",
                  },
                }));

                loadQuote(sr.last_quote_id, (quote) => {
                  setQuotes((last) => ({
                    ...last,
                    [sr.id]: quote,
                  }));
                });
              });

              setSubmitting(false);
            }
          );
        }

        toggleFlowModal(true);
      }
    }, [editingOrder]);

    // Slot-based commissions state (Split Commission feature):
    //   commissions[itemId].slots = [
    //     { key, user_type, user, percentage, add_to_cost, payment_status,
    //       currency, method, date, card_type, fee, bank_account_id, payment_id }
    //   ]
    // Up to 3 slots per item; amount is derived (% × itemProfit), not stored.
    const userOption = (u) => {
      if (!u?.id) return null;
      const composed =
        u.full_name ||
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.name ||
        u.email ||
        "";
      return { id: u.id, name: composed };
    };

    // Slot-1 default user resolution per Alex's rule:
    //   - request.created_by if they're is_sales_admin
    //   - otherwise the currently logged-in admin
    // Persisted slots (from localStorage / future API) skip this entirely.
    const resolveDefaultSlotUser = () => {
      // Default the commission to the request's HANDLER (its creator), never the
      // current viewer — otherwise a master admin opening someone's request would
      // be wrongly credited. If there's no handler, leave it unset for the admin.
      return defaultCommissionUser ? userOption(defaultCommissionUser) : null;
    };

    const defaultStoreAdminSlot = () => ({
      key: `slot-${Math.random()}`,
      user_type: "store_admin",
      user: resolveDefaultSlotUser(),
      percentage: 100,
      add_to_cost: false,
      payment_status: "created",
      currency: "gbp",
      method: "offline",
      fee: 0,
    });

    const initCommisions = (order) => {
      const stored = loadCommissionsFromStorage(order?.id);
      setCommissions(
        order?.items?.reduce((acc, item) => {
          const persisted = (item.commissions ?? []).map((commission) => ({
            key: commission.id ?? `slot-${Math.random()}`,
            user_type: commission.user_type ?? "store_admin",
            user: commission.user_id
              ? { id: commission.user_id, name: commission.user_name }
              : null,
            percentage: commission.profit_share_percentage ?? 100,
            add_to_cost: !!commission.add_to_cost,
            payment_id: commission.payment_id,
            payment_status: commission.payment?.status ?? "created",
            currency: (commission.payment?.currency ?? "gbp").toLowerCase(),
            date: commission.payment?.date || undefined,
            method: commission.payment?.method ?? "offline",
            card_type: commission.payment?.card_type,
            fee: (commission.payment?.fee || 0) / 100,
            bank_account_id: commission.payment?.bank_account_id,
          }));
          // Resolution order:
          //   1. Persisted commissions returned by the backend (real
          //      truth, ships in PR #2).
          //   2. localStorage snapshot from this session (PR #1 demo).
          //   3. Fresh default slot.
          // initCommisions used to run UNCONDITIONALLY on every mount,
          // overwriting whatever localStorage held with a default slot
          // — that's why Save & Proceed appeared not to persist.
          const storedSlots = stored?.[item.id]?.slots;
          acc[item.id] = {
            slots:
              persisted.length > 0
                ? persisted
                : storedSlots?.length
                ? storedSlots
                : [defaultStoreAdminSlot()],
          };

          return acc;
        }, {})
      );
    };

    const toggleFlowModal = (open) => {
      setFlowModalOpen(open);

      if (editingOrder && !open) {
        closeEditingOrder();
      }
    };

    // Local-only persistence for the Split Commission slots. Real
    // server-side persistence lives in PR #2 (migration + extended
    // /register-commissions DTO). Until then, we keep the slot state
    // alive across reloads via localStorage keyed by order id so reps
    // can demo the flow without losing their work to a refresh. Scoped
    // to one browser; nothing hits the API.
    const commissionsStorageKey = (orderId) =>
      orderId ? `commissions:order:${orderId}` : null;

    const loadCommissionsFromStorage = (orderId) => {
      const key = commissionsStorageKey(orderId);
      if (!key) return null;
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    // Seed a default Store-Admin slot for any order item that doesn't
    // yet have one. initCommisions only fires through the payment flow,
    // so a rep opening COMMISSIONS via V2 nav on an existing order
    // would otherwise see empty slot lists. Hydrate from localStorage
    // first (PR #1 demo persistence), then fall back to a default slot.
    useEffect(() => {
      const items = order?.items;
      if (!items || items.length === 0) return;
      const stored = loadCommissionsFromStorage(order?.id);
      setCommissions((prev) => {
        let mutated = false;
        const next = { ...prev };
        items.forEach((item) => {
          if (next[item.id] && next[item.id].slots?.length) return;
          const storedItem = stored?.[item.id];
          if (storedItem?.slots?.length) {
            next[item.id] = storedItem;
          } else {
            next[item.id] = {
              ...(next[item.id] || {}),
              slots: [defaultStoreAdminSlot()],
            };
          }
          mutated = true;
        });
        return mutated ? next : prev;
      });
    }, [order?.id, order?.items?.map((i) => i.id).join(",")]);

    // Mirror every commissions edit to localStorage so reload keeps it.
    // Guard: skip writes while commissions is still empty (initial mount,
    // before the seed effect has run) — otherwise an immediate `{}` write
    // would wipe a previously-stored payload before we get to read it,
    // and the seed effect would then fall back to defaults.
    useEffect(() => {
      const key = commissionsStorageKey(order?.id);
      if (!key) return;
      if (!commissions || Object.keys(commissions).length === 0) return;
      try {
        window.localStorage.setItem(key, JSON.stringify(commissions));
      } catch {
        // Quota / private mode — silently drop; non-essential for PR #1.
      }
    }, [commissions, order?.id]);

    // STRIPE LINK rows that aren't yet completed (the customer hasn't paid
    // through the link) are NOT real money in the bank — exclude them
    // from paidAmount so the savePayments routing and the "Total Paid By
    // Client" display don't misrepresent pending links as collected funds.
    // Once the webhook completes the Payment, payment_status flips to
    // 'completed' and the row re-enters the sum.
    // Also covers POS (stripe_terminal): a charge that hasn't completed (still
    // processing / failed) isn't money in the bank either, so exclude it from
    // paidAmount until the webhook flips payment_status to 'completed'.
    const isUncollectedStripeLink = (row) =>
      (row?.method === "stripe_link" || row?.method === "stripe_terminal") &&
      row?.payment_status !== "completed";

    const paidAmount = useMemo(() => {
      let paid = isUncollectedStripeLink(totalRemainingPaid)
        ? 0
        : convertAmount(
            rates,
            parseFloat(totalRemainingPaid.amount || 0),
            totalRemainingPaid.currency
          );

      paid += Object.values(deposits).reduce((paid, listingDeposits) => {
        const listingDepositsAmount = listingDeposits.reduce((sum, deposit) => {
          if (isUncollectedStripeLink(deposit)) return sum;
          const depositAmount = convertAmount(
            rates,
            parseFloat(deposit.amount || 0),
            deposit.currency
          );

          return sum + depositAmount;
        }, 0);

        return paid + listingDepositsAmount;
      }, 0);

      paid += marketplaceFee * 1;

      return Math.round(paid * 100);
    }, [totalRemainingPaid, deposits, rates, marketplaceFee]);

    // Every COLLECTED payment row in its ORIGINAL currency (pence), no FX
    // applied: amount and currency read off the same record so a consumer can
    // never mislabel one with the other.
    //
    // INVARIANT (real case: order 87362) — `paidAmount` above is already
    // converted into `listingRate` (the deal currency), so it may only ever be
    // labelled with `listingRate`. The Order Summary card labelled it with the
    // quote's currency instead: a 26,850 EUR payment against a GBP-resolved
    // order became "€22,976", and the base bracket then converted the already-
    // converted figure a second time ("(£19,662)"). Consumers that need to say
    // what the CLIENT paid sum these rows instead. Marketplace fee is
    // deliberately excluded — it is not a client payment.
    const paymentRows = useMemo(() => {
      const rows = [];
      const push = (row) => {
        const amount = parseFloat(row?.amount || 0);
        if (!amount || isUncollectedStripeLink(row)) return;
        rows.push({
          amount: toPence(amount),
          // A payment row with no currency of its own is LEGACY GBP — the same
          // rule convertAmount() applies when paidAmount sums these rows (its
          // `from` defaults to 'gbp'). Falling back to the STORE BASE here
          // instead made the two disagree: on a non-GBP-base store a legacy row
          // was summed as GBP but labelled with the base.
          currency: (row.currency || "gbp").toLowerCase(),
        });
      };
      Object.values(deposits || {}).forEach((listingDeposits) =>
        (listingDeposits || []).forEach(push)
      );
      push(totalRemainingPaid);
      return rows;
    }, [deposits, totalRemainingPaid]);

    // Sum of every populated row, INCLUDING stripe_link drafts that
    // `paidAmount` deliberately excludes. Drives the bottom-action button
    // label and the save-routing switch — a populated stripe_link row is
    // still "something the rep wants to save" even though it's not yet
    // collected money. Without this, a stripe_link-only row leaves
    // `paidAmount === 0`, the UI falls back to "Add to cart", and
    // savePayments routes through createNewUnpaidOrder which never
    // persists the draft Payment (regression introduced by Path A).
    const populatedAmount = useMemo(() => {
      let total = convertAmount(
        rates,
        parseFloat(totalRemainingPaid.amount || 0),
        totalRemainingPaid.currency
      );

      total += Object.values(deposits).reduce((acc, listingDeposits) => {
        return (
          acc +
          listingDeposits.reduce((sum, deposit) => {
            return (
              sum +
              convertAmount(
                rates,
                parseFloat(deposit.amount || 0),
                deposit.currency
              )
            );
          }, 0)
        );
      }, 0);

      total += marketplaceFee * 1;

      return Math.round(total * 100);
    }, [totalRemainingPaid, deposits, rates, marketplaceFee]);

    const toPay = useMemo(() => {
      const listingsPrice = listings.reduce(
        (sum, listing) =>
          // DELIBERATELY UNCONVERTED. `toPay` is what the order will be BILLED
          // at, and the backend builds that figure the same way:
          // OrderManagementService::registerOrder sets
          // `total_amount = Σ listing->price` (no FX). Converting here would
          // print a total no invoice will ever show.
          //
          // Because the sum is raw listing prices, the currency that labels it
          // is the LISTINGS' own price currency — which is what `listingRate`
          // resolves to (see its comment). Passing `listingRate` as the source
          // currency makes rates[listingRate] === 1, i.e. this convertAmount is
          // an intentional pass-through; it is NOT a per-listing conversion, and
          // a mixed-currency cart is rejected on save by OrderValidationService
          // rather than summed here.
          //
          // The residual mismatch — a GBP listing price sold on a non-GBP quote
          // — is a BACKEND pairing defect (orders.currency is stamped
          // quote-first); the Order Summary card's amber "quote does not match
          // the listing price" notice is what surfaces it to the rep.
          sum +
          convertAmount(rates, parseFloat(listing.price), listingRate),
        0
      );

      const sellRequestsPrice = sellRequests.reduce((sum, sr) => {
        // Fall back to sr.last_quote when om.quotes hasn't been
        // seeded for this sell request yet — common on a freshly
        // loaded px-order page where useRequest provides the sell
        // requests via initialSellRequests but their quote rows
        // haven't been hydrated into om.quotes. Without this fallback
        // sellRequestsPrice collapses to 0 and toPay shows the full
        // listing price instead of (listing − trade-in).
        const quote = quotes[sr.id] ?? sr.last_quote;

        return (
          sum +
          convertAmount(rates, parseFloat(quote?.price || 0), quote?.currency)
        );
      }, 0);

      return listingsPrice - sellRequestsPrice;
    }, [listings, quotes, sellRequests, rates]);

    const sellRequestsAmount = useMemo(() => {
      return sellRequests.reduce((sum, sr) => {
        const quote = quotes[sr.id] ?? sr.last_quote;

        return (
          sum +
          convertAmount(rates, parseFloat(quote?.price || 0), quote?.currency)
        );
      }, 0);
    }, [sellRequests, quotes, rates]);

    // The single figure the UPDATE LISTING PRICE dialog promises and
    // `updateListingPrice` writes. Defined once so the two cannot diverge: the
    // dialog rendered `paidAmount + sellRequestsAmount` while the handler
    // recomputed the same sum, and both were floats — <Money> rounds the
    // display to 2dp (whole minor units) while the PUT sent the unrounded
    // value, so the row landed a fraction of a penny off the confirmed number.
    //
    // Minor units, denominated in `listingRate`: `paidAmount` is converted into
    // it and `sellRequestsAmount` is built with the same rates. That is why the
    // PUT must STATE that currency — without it the backend leaves
    // listings.currency as it was (NULL ⇒ read as legacy GBP everywhere else)
    // and skips the gbp_price recompute, so the new price keeps the OLD price's
    // GBP conversion in every report.
    const newListingPrice = useMemo(
      () => Math.round(paidAmount + sellRequestsAmount),
      [paidAmount, sellRequestsAmount]
    );

    const fees = useMemo(() => {
      const cardFees =
        // "offile" was a typo, so this test was ALWAYS true: an offline Total
        // Remaining Paid row's fee was counted as a card fee here AND as a bank
        // fee below, double-counting it in TOTAL FEES / TOTAL PAID AFTER FEES.
        (!isNonCardFeeMethod(totalRemainingPaid.method)
          ? convertAmount(
              rates,
              parseFloat(totalRemainingPaid.fee || 0),
              totalRemainingPaid.currency
            )
          : 0) +
        Object.values(deposits).reduce((sum, listingDeposits) => {
          return (
            sum +
            listingDeposits.reduce((fee, deposit) => {
              if (isNonCardFeeMethod(deposit.method)) {
                return fee;
              }

              return (
                fee +
                convertAmount(
                  rates,
                  parseFloat(deposit.fee || 0),
                  deposit.currency
                )
              );
            }, 0)
          );
        }, 0);

      const bankFees =
        (isNonCardFeeMethod(totalRemainingPaid.method)
          ? convertAmount(
              rates,
              parseFloat(totalRemainingPaid.fee || 0),
              totalRemainingPaid.currency
            )
          : 0) +
        Object.values(deposits).reduce((sum, listingDeposits) => {
          return (
            sum +
            listingDeposits.reduce((fee, deposit) => {
              if (!isNonCardFeeMethod(deposit.method)) {
                return fee;
              }

              return (
                fee +
                convertAmount(
                  rates,
                  parseFloat(deposit.fee || 0),
                  deposit.currency
                )
              );
            }, 0)
          );
        }, 0);

      return {
        card: Math.round(cardFees * 100),
        bank: Math.round(bankFees * 100),
      };
    }, [totalRemainingPaid, deposits, rates]);

    // Per-item profit (in pence) = sale price minus buying + other costs,
    // EXCLUDING the order's expert fee. Marketplace fee is whole-order so it's
    // excluded here too (reflected in the footer figure below).
    //
    // Not used on its own — `itemProfit` below adds the expert fee to it, and
    // that is what every consumer reads.
    const itemProfitExclExpertFee = (item) => {
      if (!item) return 0;
      const listing = item.listing || {};
      // Every cost term below is a GBP PIVOT column, so the sale price has to be
      // converted into GBP before subtracting them — otherwise a non-GBP order's
      // profit is overstated (a $9,300 sale must not be treated as £9,300 against
      // a £6,462 cost). The bug this replaces was subtler than that: the rate
      // came from `listingRate`, which was the order's STORED currency, so on
      // every order still carrying the defaulted 'gbp' the rate was 1 while the
      // costs were converted properly — order 87362's Net Profit converted €300
      // of costs at 0.8557 and the €26,850 sale at 1.0, two different rates
      // inside one subtraction, overstating profit by £3,874.
      //
      // Convert from the currency `item.price` is genuinely in — resolved off
      // the record that describes that amount (see orderItemCurrency) — using
      // the ccy→gbp table. gbpRates has no row for GBP itself, so a GBP item is
      // ×1 and the flag-off path is byte-identical.
      const price = convertAmount(
        gbpRates,
        Number(item.price) || 0,
        orderItemCurrency(item, listing)
      );
      const buying = Number(listing.gbp_buying_price || 0);
      const polishing = Number(listing.gbp_polishing || 0);
      const accessories = Number(listing.gbp_accessories || 0);
      const shipping = Number(listing.gbp_shipping || 0);
      const travel = Number(listing.gbp_travel || 0);
      return price - buying - polishing - accessories - shipping - travel;
    };

    // The EXPERT FEE in GBP pence, or 0 when the row isn't a complete record.
    //
    // PRODUCT RULE (Alex, 2026-08-07): the expert fee is money the client paid
    // ON TOP of the watch — REVENUE, not a cost. It raises the deal's profit
    // and therefore the pool the commission percentages are taken from. It is
    // NOT subtracted anywhere: adding it to the profit and then deducting it
    // again is what would put a fee-carrying order into a phantom loss.
    //
    // It counts only once the rep has entered an amount AND said which expert
    // it relates to. An amount with no expert is a half-typed row and must not
    // move profit — the backend applies the identical test in
    // ExpertFee::amountGbpRaw, so the card and the P&L can never disagree.
    //
    // The amount alone: this row has no transaction-fee field (unlike deposits,
    // TRP and commission slots).
    //
    // GBP because every consumer (itemProfit below, the card's Net Profit) is a
    // GBP pivot: the row's own currency converts with the ccy→gbp table, which
    // has no GBP row, so a GBP fee is ×1.
    const expertFeeGbp = useMemo(() => {
      if (!expertFee?.expert_account_id) return 0;
      const amount = parseFloat(expertFee.amount || 0);
      if (!amount) return 0;
      return (
        amount *
        100 *
        (lookupRate(gbpRates, String(expertFee.currency || "gbp")) || 1)
      );
    }, [expertFee, gbpRates]);

    /**
     * This item's slice of the order-level EXPERT FEE, in GBP pence.
     *
     * The fee is recorded once for the whole order but profit is per item, so
     * it has to be apportioned before it can join one. Split by OUTSTANDING
     * BALANCE (item price − its own deposits) — deliberately the same rule
     * OrderExportService::orderReceiptAllocation uses for the P&L column, so a
     * multi-watch order apportions the fee the same way on both surfaces.
     * Single-item orders (the overwhelming majority) get 100% either way.
     *
     * Falls back to an even split when nothing is outstanding, matching the
     * backend's `1 / count`.
     */
    const expertFeeShareGbp = (item) => {
      const items = order?.items || [];
      if (!item || !items.length || !expertFeeGbp) return 0;
      if (items.length === 1) return expertFeeGbp;

      const outstanding = (it) => {
        const listing = it.listing || {};
        const price = convertAmount(
          gbpRates,
          Number(it.price) || 0,
          orderItemCurrency(it, listing)
        );
        const paid = (deposits[it.listing_id] || []).reduce(
          (sum, d) =>
            sum +
            convertAmount(gbpRates, parseFloat(d.amount || 0) * 100, d.currency),
          0
        );
        return Math.max(0, price - paid);
      };

      const total = items.reduce((sum, it) => sum + outstanding(it), 0);
      if (!total) return expertFeeGbp / items.length;

      return expertFeeGbp * (outstanding(item) / total);
    };

    /**
     * Per-item profit, WITH the item's share of the expert fee added.
     *
     * PRODUCT RULE (Alex, 2026-08-07): the expert fee joins the item profit
     * before commissions are calculated. The fee is revenue the client paid on
     * top, so a £2,000 item carrying a £250 expert fee is a £2,250 deal — and
     * the commission percentages, which total 100%, are taken from £2,250.
     *
     * This is the one profit figure every consumer reads: the Split Commission
     * slot amounts, commissionsAmount, prepareCommissions and
     * netProfitBeforeCommissions. Keeping it single is what stops the fee being
     * counted in one place and not another.
     */
    const itemProfit = (item) =>
      itemProfitExclExpertFee(item) + expertFeeShareGbp(item);

    // commissionsAmount = sum of (slot.percentage / 100 * itemProfit) plus
    // any FX/card fee entered for that slot, for every slot where
    // add_to_cost is ON, across every order item. Slots with add_to_cost
    // OFF still hold a % split but don't dent profit. Fee is entered in
    // pounds; convert to pence to stay on the same scale as profit.
    //
    // The order's EXPERT FEE is NOT added here. It reaches this figure through
    // `itemProfit`, which the percentages are applied to — a £250 fee on a
    // £2,000 item raises the base to £2,250, so slots totalling 100% allocate
    // £2,250. Adding the fee again on top would allocate £2,500 of a £2,250
    // deal and push every fee-carrying order into a phantom loss.
    //
    // The whole figure is GBP: itemProfit is GBP (it converts the sale price via
    // sellingToGbpRate against GBP-pivot costs), so the slot's fee — entered in
    // the SLOT's own currency, like commissionFees below reads it — is converted
    // into GBP with the ccy→gbp table before it joins the sum. Adding it raw
    // dropped an EUR/USD fee straight into a GBP total, and that total is what
    // the card's Commissions and Net Profit rows render. gbpRates has no row for
    // GBP itself, so a default (GBP) slot is ×1 — byte-identical.
    const commissionsAmount = useMemo(() => {
      const orderItems = order?.items || [];
      return orderItems.reduce((sum, item) => {
        const slots = commissions?.[item.id]?.slots ?? [];
        const profit = itemProfit(item);
        const itemSum = slots.reduce((s, slot) => {
          if (!slot?.add_to_cost) return s;
          const pct = Number(slot.percentage) || 0;
          const share = (pct / 100) * profit;
          const fee =
            (Number(slot.fee) || 0) *
            100 *
            (lookupRate(gbpRates, String(slot.currency || "gbp")) || 1);
          return s + share + fee;
        }, 0);
        return sum + itemSum;
      }, 0);
      // expertFeeGbp is in the deps because itemProfit above depends on it —
      // without it the slot amounts would not recompute when the fee changes.
    }, [commissions, order, rates, gbpRates, sellingToGbpRate, expertFeeGbp]);

    const commissionFees = useMemo(() => {
      const orderItems = order?.items || [];
      const fees = orderItems.reduce(
        (acc, item) => {
          const slots = commissions?.[item.id]?.slots ?? [];
          slots.forEach((slot) => {
            if (!slot?.add_to_cost) return;
            const fee = convertAmount(
              rates,
              parseFloat(slot.fee || 0),
              slot.currency
            );
            if (isNonCardFeeMethod(slot.method)) acc.bank += fee;
            else acc.card += fee;
          });
          return acc;
        },
        { card: 0, bank: 0 }
      );
      return {
        card: Math.round(fees.card * 100),
        bank: Math.round(fees.bank * 100),
      };
    }, [commissions, order, rates]);

    // Whole-order Net Profit BEFORE Commissions — used by the Commissions
    // footer label and as the base figure for any per-order rollup.
    //
    // Sums itemProfit, which INCLUDES the expert fee — the fee is revenue the
    // client paid on top, so it belongs in the order's profit. commissionsAmount
    // is then subtracted from this, and the two are computed off the same base:
    // slots totalling 100% land the figure on zero, which is the point.
    const netProfitBeforeCommissions = useMemo(() => {
      const orderItems = order?.items || [];
      const itemsProfit = orderItems.reduce(
        (sum, item) => sum + itemProfit(item),
        0
      );
      // itemsProfit is GBP (itemProfit converts each item's price out of that
      // item's own currency via gbpRates — hence gbpRates in the deps, or this
      // would not recompute when the rates land). The marketplace fee is typed
      // into the form in the ORDER currency, so it converts with
      // sellingToGbpRate; that is the one term here for which listingRate IS the
      // right source, because the field's symbol is drawn from it too.
      // Flag-off ⇒ both rates 1 ⇒ unchanged.
      const fee = Math.round(
        (Number(marketplaceFee) || 0) * 100 * (sellingToGbpRate || 1)
      );
      return itemsProfit - fee;
    }, [order, marketplaceFee, sellingToGbpRate, gbpRates]);

    // FX RULE (hooks/fx.js): true when ANY amount above needs an exchange rate
    // we don't have (the request failed and none was ever known, or it is
    // still loading). The totals were then computed with a stand-in of 1, so
    // consumers must not show them and the saves below refuse to run. Same
    // currency on both sides never needs a rate.
    const fxMissing = useMemo(() => {
      // needs(table, target, from, amount): see needsMissingRate in hooks/fx.
      const depositRows = Object.values(deposits || {}).flat();
      const paymentRowsMissing = [totalRemainingPaid, ...depositRows].some(
        (row) =>
          needs(rates, listingRate, row?.currency, row?.amount) ||
          needs(rates, listingRate, row?.currency, row?.fee)
      );
      const quotesMissing = (sellRequests || []).some((sr) => {
        const quote = quotes[sr.id] ?? sr.last_quote;
        return needs(rates, listingRate, quote?.currency, quote?.price);
      });
      const items = order?.items || [];
      const itemsMissing = items.some((item) =>
        needs(gbpRates, "gbp", orderItemCurrency(item, item.listing || {}), item.price)
      );
      const expertMissing =
        !!expertFee?.expert_account_id &&
        (needs(gbpRates, "gbp", expertFee.currency || "gbp", expertFee.amount) ||
          // A multi-item order splits the fee by outstanding balance, which
          // converts each deposit into GBP.
          (items.length > 1 &&
            depositRows.some((d) =>
              needs(gbpRates, "gbp", d?.currency, d?.amount)
            )));
      const slotsMissing = Object.values(commissions || {}).some((c) =>
        (c?.slots ?? []).some(
          (slot) =>
            // The GBP share is re-expressed in the slot's payout currency.
            needs(gbpRates, "gbp", slot?.currency || "gbp", slot?.percentage) ||
            (!!slot?.add_to_cost &&
              (needs(gbpRates, "gbp", slot?.currency || "gbp", slot?.fee) ||
                needs(rates, listingRate, slot?.currency, slot?.fee)))
        )
      );
      const marketplaceFeeMissing =
        !!Number(marketplaceFee) && sellingToGbpRate == null;
      return (
        paymentRowsMissing ||
        quotesMissing ||
        itemsMissing ||
        expertMissing ||
        slotsMissing ||
        marketplaceFeeMissing
      );
    }, [
      deposits,
      totalRemainingPaid,
      sellRequests,
      quotes,
      order,
      expertFee,
      commissions,
      marketplaceFee,
      rates,
      gbpRates,
      listingRate,
      sellingToGbpRate,
    ]);
    // "Rate unavailable" (failed) vs "…" (first request still in flight).
    const fxUnavailable =
      fxMissing &&
      ratesStatus !== "loading" &&
      gbpRatesStatus !== "loading" &&
      !(
        !!Number(marketplaceFee) &&
        sellingToGbpRate == null &&
        !sellingToGbpUnavailable
      );
    const FX_SAVE_BLOCKED =
      "Exchange rate unavailable, so the converted totals can't be checked. Nothing was saved. Try again shortly.";

    const arePaymentsValid = useMemo(() => {
      const depositsValid = Object.values(deposits).every((listingDeposits) => {
        return listingDeposits.every((deposit) => {
          if (!deposit.amount) return true;
          // STRIPE LINK and POS rows don't need date/bank_account_id at save
          // time: date auto-populates from the Stripe webhook on success and
          // the bank account is optional. Allow save so the rep can then click
          // Generate link / Charge on Reader.
          if (
            deposit.method === "stripe_link" ||
            deposit.method === "stripe_terminal"
          )
            return true;
          // The bank account is the Xero account the money settles into; a
          // natively invoiced store has none, so only the date is asked for.
          return deposit.date && (nativeInvoice || deposit.bank_account_id);
        });
      });

      return (
        depositsValid &&
        (!totalRemainingPaid.amount ||
          totalRemainingPaid.method === "stripe_link" ||
          totalRemainingPaid.method === "stripe_terminal" ||
          (totalRemainingPaid.date &&
            (nativeInvoice || totalRemainingPaid.bank_account_id)))
      );
    }, [deposits, totalRemainingPaid, nativeInvoice]);

    const allPaymentsConfirmed = useMemo(() => {
      const allDepositsConfirmed = Object.values(deposits).every(
        (listingDeposits) => {
          return listingDeposits.every(
            (deposit) =>
              !deposit.amount || deposit.payment_status === "completed"
          );
        }
      );

      return (
        allDepositsConfirmed &&
        totalRemainingPaid.payment_status === "completed"
      );
    }, [deposits, totalRemainingPaid]);

    const clearError = () => {
      setError(null);
    };

    const onToggleListing = (listing) => {
      updateTaxRate("listings", listing.id, listing.tax_rate, listing.id);

      setListings((listings) => {
        const index = listings.findIndex((l) => l.id === listing.id);
        if (index === -1) {
          if (listings.length >= MAX_LISTINGS) {
            return listings;
          }

          const errorMsg = validateAssignedListing(listing);
          if (errorMsg) {
            setError(errorMsg);

            return listings;
          }

          setDeposits((prevDeposits) => ({
            ...prevDeposits,
            [listing.id]: [
              {
                key: Math.random(),
                amount: 0,
                currency: baseCurrency,
                method: "offline",
              },
            ],
          }));

          return [...listings, listing];
        }

        setDeposits((prevDeposits) => {
          const newDeposits = { ...prevDeposits };
          delete newDeposits[listing.id];

          return newDeposits;
        });

        return [...listings.slice(0, index), ...listings.slice(index + 1)];
      });
    };

    const onSelectSellRequest = (sellRequest) => {
      updateTaxRate(
        "requests",
        sellRequest.id,
        sellRequest.listing?.tax_rate,
        sellRequest.listing_id
      );

      setSellRequests((sellRequests) => {
        const index = sellRequests.findIndex((sr) => sr.id === sellRequest.id);
        if (index === -1) {
          if (sellRequests.length >= MAX_SELL_REQUESTS) {
            return sellRequests;
          }

          return [...sellRequests, sellRequest];
        }

        return [
          ...sellRequests.slice(0, index),
          ...sellRequests.slice(index + 1),
        ];
      });
    };

    const handleSetQuotes = async (newQuotes) => {
      const changedRuestId = Object.keys(newQuotes).find(
        (id) => newQuotes[id]?.id != quotes[id]?.id
      );

      setQuotes(newQuotes);

      await axios.put(`/api/requests/${changedRuestId}/assign-quote`, {
        last_quote_id: newQuotes[changedRuestId]?.id,
      });
    };

    // Set when a SAVED deposit row (one with a payment_id) is dropped from the
    // form by clearing its amount — see hasExistingDeposits in savePayments.
    // Cleared once a save has reconciled the server against the form.
    const droppedSavedDepositRef = useRef(false);

    const updateDepositField = (listingId, idx, field, value) => {
      setDeposits((deposits) => {
        const listingDeposits = [...(deposits[listingId] || [])];

        if (
          listingDeposits.length < 3 &&
          idx + 1 === listingDeposits.length &&
          listingDeposits[idx].amount === 0 &&
          field === "amount" &&
          value > 0
        ) {
          // Add a new empty deposit row
          listingDeposits.push({
            payment_status: "created",
            key: Math.random(),
            amount: 0,
            currency: baseCurrency,
            method: "offline",
          });
        }

        if (field !== "amount" || value > 0) {
          // Just update the field. Picking a currency STICKS: mark the row so
          // the base-currency default can never overwrite the admin's choice,
          // even while the amount is still empty (see currencyIsAdminOwned).
          listingDeposits[idx] = {
            ...listingDeposits[idx],
            [field]: value,
            ...(field === "currency" ? { currency_touched: true } : {}),
          };
        } else if (
          idx + 1 < listingDeposits.length ||
          listingDeposits.length === 3
        ) {
          if (listingDeposits.length === 3 && listingDeposits[2].amount > 0) {
            // Add a new empty deposit row
            listingDeposits.push({
              payment_status: "created",
              key: Math.random(),
              amount: 0,
              currency: baseCurrency,
              method: "offline",
            });
          }

          // Here the amount introduced was 0, so we remove the row
          if (listingDeposits[idx]?.payment_id) {
            droppedSavedDepositRef.current = true;
          }
          listingDeposits.splice(idx, 1);
        }

        const newDeposits = {
          ...deposits,
          [listingId]: listingDeposits,
        };

        return newDeposits;
      });
    };

    // Fully drop a deposit row after its payment was removed server-side.
    // Splicing the row discards its stale `payment_id` (so a re-entered deposit
    // can't resend a removed/soft-deleted id), and we guarantee at least one
    // empty input row remains so the rep can immediately enter a new deposit
    // for the same product without a page refresh.
    const removeDepositRow = (listingId, idx) => {
      setDeposits((deposits) => {
        const listingDeposits = [...(deposits[listingId] || [])];
        if (!listingDeposits[idx]) return deposits;

        listingDeposits.splice(idx, 1);

        const hasEmptyRow = listingDeposits.some((d) => !d.amount);
        if (!hasEmptyRow && listingDeposits.length < 3) {
          listingDeposits.push({
            payment_status: "created",
            key: Math.random(),
            amount: 0,
            currency: baseCurrency,
            method: "offline",
          });
        }

        return {
          ...deposits,
          [listingId]: listingDeposits,
        };
      });
    };

    const updateTaxRate = async (type, id, rate, listingId) => {
      // Update UI immediately so the toggle feels instant
      setTaxRates((prev) => ({
        ...prev,
        [type]: { ...prev[type], [id]: rate },
      }));
      try {
        await updateListingTaxRate(listingId, rate);
      } catch (error) {
        // Revert on failure
        setTaxRates((prev) => ({
          ...prev,
          [type]: { ...prev[type], [id]: prev[type]?.[id] },
        }));
      }
    };

    const setTotalRemainingPaidField = (field, value) => {
      setTotalRemainingPaid((totalRemainingPaid) => ({
        ...totalRemainingPaid,
        [field]: value,
        // Same sticky rule as the deposit rows: an explicitly picked currency
        // outranks the base-currency default from here on.
        ...(field === "currency" ? { currency_touched: true } : {}),
      }));
    };

    // ── Live settle of a paid STRIPE LINK ────────────────────────────────
    // A STRIPE LINK row is money in the bank only once Stripe's webhook
    // completes the Payment — exactly what isUncollectedStripeLink above
    // encodes, and its own comment already promised "once the webhook
    // completes the Payment, payment_status flips to 'completed' and the row
    // re-enters the sum". Nothing ever told this page the flip had happened,
    // so a rep with the order open kept seeing the row PENDING, TOTAL PAID BY
    // CLIENT unchanged and the Order Summary status stale until a full reload.
    //
    // POS solves the identical problem by polling the payment until the
    // webhook resolves it (PaymentRow.pollPosStatus). Links never got an
    // equivalent because the client can pay minutes or hours later, so there
    // is no charge to poll "until" — the answer is to poll only while a rep is
    // actually looking at an order that HAS an unpaid link.
    //
    // Four deliberate limits, so this can never become a background tax:
    //   · only while at least one row is an uncollected link WITH a link id;
    //   · only while the tab is visible (a hidden tab reschedules, never fetches);
    //   · off /payment-links — the endpoint the GENERATED LINKS table already
    //     calls, so no new backend surface, and the row already carries the
    //     payment_link_id needed to match a link back to its row;
    //   · it stops itself: settling the row removes it from pendingLinkTargets,
    //     which is this effect's own dependency, so the timer tears down.
    //
    // What it writes is ONE field per settled row. Everything the rep sees
    // recomputes from it — paidAmount, paymentRows, the section CTAs, and the
    // smart card's Total Paid / Balance Due — and a settled row is read-only,
    // so nothing anyone may be mid-edit is touched.
    const pendingLinkTargets = useMemo(() => {
      const targets = new Map();
      const isPendingLink = (row) =>
        row?.method === "stripe_link" &&
        row?.payment_status !== "completed" &&
        !!row?.payment_link_id;

      Object.entries(deposits || {}).forEach(([listingId, rows]) => {
        (rows || []).forEach((row, idx) => {
          if (isPendingLink(row)) {
            targets.set(String(row.payment_link_id), (field, value) =>
              updateDepositField(listingId, idx, field, value)
            );
          }
        });
      });

      if (isPendingLink(totalRemainingPaid)) {
        targets.set(
          String(totalRemainingPaid.payment_link_id),
          (field, value) => setTotalRemainingPaidField(field, value)
        );
      }

      return targets;
      // updateDepositField / setTotalRemainingPaidField are stable per render
      // and only ever WRITE, so re-deriving on the two state values is enough.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deposits, totalRemainingPaid]);

    // Read at tick time rather than closed over, so a settle always writes to
    // the row's CURRENT index even if the rows re-ordered since the timer began.
    const pendingLinkTargetsRef = useRef(pendingLinkTargets);
    pendingLinkTargetsRef.current = pendingLinkTargets;

    // Held in a ref and deliberately NOT a dependency of the poll below.
    // useRequest.refreshRequest is redefined on every render and V2 passes it
    // straight through, so depending on it would tear the timer down and
    // restart it on every re-render of the request page — i.e. on every
    // keystroke in any field — and a 10s timer that restarts that often never
    // fires. The poll must survive re-renders; only the order and the set of
    // outstanding links may restart it.
    const onRequestRefreshRef = useRef(onRequestRefresh);
    onRequestRefreshRef.current = onRequestRefresh;

    // Bumped once per settle. Drives the GENERATED LINKS refetch in Payments
    // and the delayed invoice refresh below.
    const [linkSettleCount, setLinkSettleCount] = useState(0);

    // usePoll (M4.1): a setTimeout chain at the same 10 s. A failed call
    // (offline, 500) backs off instead of retrying at full speed, 401/403/404
    // ("endpoint not deployed") stop it, and a hidden tab asks nothing — it
    // asks once on return. Still silent: the rep did not ask for this request.
    const linkPollOrderId = order?.id;
    const linkPollOrderIdRef = useRef(linkPollOrderId);
    linkPollOrderIdRef.current = linkPollOrderId;

    const pollStripeLinks = useCallback(async () => {
      const orderId = linkPollOrderId;
      const { data } = await axios.get(
        `/api/admin/orders/${orderId}/payment-links`,
        { metadata: { silent: true } }
      );
      // The page moved to another order while this was in flight.
      if (linkPollOrderIdRef.current !== orderId) return;
      const links = Array.isArray(data) ? data : data?.data || [];
      const targets = pendingLinkTargetsRef.current;
      const settled = links.filter(
        (link) => link?.status === "paid" && targets.has(String(link.id))
      );
      if (!settled.length) return;

      settled.forEach((link) => {
        const apply = targets.get(String(link.id));
        apply("payment_status", "completed");
        apply("payment_link_status", "paid");
      });

      // request.status is already correct server-side: OrderObserver
      // cascades it the moment markPaid flips order.status, in the
      // webhook itself. This only pulls it into the page so the Order
      // Summary / smart-card badge stops showing the stale value.
      try {
        await onRequestRefreshRef.current?.();
      } catch {
        return; // as before: a failed refresh skips the settle bump
      }
      setLinkSettleCount((n) => n + 1);
      // Settling removed these rows from pendingLinkTargets, so this poll
      // is about to restart (new fn) or switch off (empty map).
      // pendingLinkTargets is a dependency on purpose: like the old effect,
      // a changed set of outstanding links restarts the 10 s chain.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkPollOrderId, pendingLinkTargets]);

    usePoll(pollStripeLinks, {
      interval: STRIPE_LINK_POLL_MS,
      enabled: !!linkPollOrderId && pendingLinkTargets.size > 0,
    });

    // The invoice PDF is rendered live from Xero on every fetch, and the
    // payment reaches Xero through a QUEUED job (RecordXeroPaymentJob), so it
    // is only correct a beat AFTER the row settles. One delayed refresh, not a
    // retry loop: if it still lands early, opening the INVOICE section
    // refetches anyway (Invoice.jsx), which is the path the rep already uses.
    // Skipped entirely when no invoice has been generated for this order.
    useEffect(() => {
      if (
        !linkSettleCount ||
        !order?.id ||
        !(order?.xero_invoice_number || order?.invoice_number)
      ) {
        return undefined;
      }

      let cancelled = false;
      const id = setTimeout(async () => {
        try {
          const { data } = await axios.get(
            `/api/order-management/${order.id}/get-pdf`,
            { metadata: { silent: true } }
          );
          if (cancelled || !data?.fileId) return;
          setGetPdf(data.fileId);
          setGetPdfUrl(data.fileUrl);
        } catch {
          // Same fallback as above — the section refetches when opened.
        }
      }, INVOICE_SETTLE_REFRESH_MS);

      return () => {
        cancelled = true;
        clearTimeout(id);
      };
    }, [linkSettleCount, order?.id, order?.xero_invoice_number, order?.invoice_number]);

    const setExpertFeeField = (field, value) => {
      setExpertFee((expertFee) => ({
        ...expertFee,
        [field]: value,
        // Mirror the TRP/deposit rule: an explicit currency pick is
        // ADMIN-OWNED and the store-base default must never rewrite it.
        ...(field === "currency" ? { currency_touched: true } : {}),
      }));
    };

    // Slot-aware field updater for the Split Commission UI. `slotIdx`
    // selects which of the (up to 3) commission slots on this item to
    // mutate; `field` is any property on a slot object (user_type, user,
    // percentage, add_to_cost, method, fee, etc.).
    const updateCommissionField = (itemId, slotIdx, field, value) => {
      setCommissions((commissions) => {
        const itemCommission = { ...(commissions[itemId] || { slots: [] }) };
        const slots = [...(itemCommission.slots || [])];
        if (!slots[slotIdx]) return commissions;
        slots[slotIdx] = { ...slots[slotIdx], [field]: value };
        return { ...commissions, [itemId]: { ...itemCommission, slots } };
      });
    };

    const addCommissionSlot = (itemId) => {
      setCommissions((commissions) => {
        const itemCommission = { ...(commissions[itemId] || { slots: [] }) };
        const slots = [...(itemCommission.slots || [])];
        if (slots.length >= 3) return commissions;
        slots.push({
          key: `slot-${Math.random()}`,
          user_type: "store_admin",
          user: null,
          percentage: 0,
          add_to_cost: false,
          payment_status: "created",
          currency: "gbp",
          method: "offline",
          fee: 0,
        });
        return { ...commissions, [itemId]: { ...itemCommission, slots } };
      });
    };

    const removeCommissionSlot = (itemId, slotIdx) => {
      setCommissions((commissions) => {
        const itemCommission = { ...(commissions[itemId] || { slots: [] }) };
        const slots = [...(itemCommission.slots || [])];
        if (slots.length <= 1) return commissions;
        slots.splice(slotIdx, 1);
        return { ...commissions, [itemId]: { ...itemCommission, slots } };
      });
    };

    const onAssignListings = async () => {
      const nextStep =
        listings.length === 1 ? STEPS.ASSIGN_SELL_REQUESTS : STEPS.PAYMENTS;
      setStep(nextStep);
      setLastStep(nextStep);
      setIsPxOrder(listings.length === 1);
      toggleFlowModal(true);

      // The listings search request doesn't return all the fields we need for the order flow,
      // so we fetch them individually here.
      const listingsWithAllFieldsPopulated = await Promise.all(
        listings.map((listing) => getListing(listing.id))
      );
      setListings(listingsWithAllFieldsPopulated);
    };

    const skipSellRequests = async () => {
      // When an existing order is already PX-linked and admin reverses
      // to "no sell requests", dissolve the PX state server-side so
      // order.px_request_id is cleared and downstream sections reflect
      // an independent (non-PX) order.
      if (order?.id && order?.px_request_id) {
        try {
          const { data: updatedOrder } = await axios.post(
            `/api/order-management/${order.id}/dissolve-px`
          );
          setOrder(updatedOrder);
          setSellRequests([]);
          await onRequestRefresh?.();
        } catch (err) {
          console.error("[RC] failed to dissolve PX", err);
        }
      }
      setIsPxOrder(false);
      setStep(STEPS.PAYMENTS);
      if (isInline) {
        setAssignSellRequestsOpen(false);
        setSellRequestsSkipped(true);
      } else {
        setLastStep(STEPS.PAYMENTS);
      }
    };

    const onRemoveSellRequest = (id) => {
      if (sellRequests.length < 2) {
        return;
      }

      setSellRequests((sellRequests) =>
        sellRequests.filter((sr) => sr.id !== id)
      );
    };

    const proceedToPayment = () => {
      setStep(STEPS.PAYMENTS);
      setLastStep(STEPS.PAYMENTS);
    };

    const goToSummary = async () => {
      // Persist the PX link server-side whenever admin picks sell
      // requests and clicks PROCEED. Two cases:
      //   (a) Cart Order already exists (non-PX or returning) — POST
      //       with order_id; backend sets order.px_request_id.
      //   (b) Cart Order doesn't exist yet (admin is on a fresh order
      //       request) — POST with request_id (parent Order Request);
      //       backend sets request.px_request_id directly.
      // Skip the POST if any selected sell already points to a
      // PxRequest — backend's PxRequestService.create handles "reuse
      // existing" semantics, and we don't want to POST with both
      // order_id and request_id (which the validator rejects).
      const someSellAlreadyLinked = sellRequests?.some(
        (sr) => sr.px_request_id
      );
      const orderAlreadyPx = !!order?.px_request_id;
      const shouldPost =
        sellRequests?.length > 0 &&
        !orderAlreadyPx &&
        !someSellAlreadyLinked;

      if (shouldPost) {
        try {
          const payload = {
            sell_requests: sellRequests.map((sr) => sr.id),
          };
          if (order?.id) {
            payload.order_id = order.id;
          } else if (parentRequestId) {
            payload.request_id = parentRequestId;
          }
          // Don't POST if we have nothing to attach to.
          if (payload.order_id || payload.request_id) {
            await axios.post("/api/px-requests", payload);
            if (order?.id) {
              const { data: updatedOrder } = await axios.get(
                `/api/orders/${order.id}`
              );
              setOrder(updatedOrder);
            }
            // Re-pull the parent request so V2 sees the new
            // px_request_id linkage and pxRequests / pxProducts
            // populate from the Design 2 overlay.
            await onRequestRefresh?.();
          }
        } catch (err) {
          console.error("[RC] failed to persist PX link on PROCEED", err);
          // Same silent failure as useRequest's FINISH upsert: without the
          // link the deal opens as a plain order + a plain sell.
          alert?.error?.(
            "The part-exchange could not be linked to this order — the trade-in is saved. Click PROCEED again to link it before taking payment.",
            { timeout: 12000 }
          );
        }
      }
      setIsPxOrder(true);
      setStep(STEPS.PX_SUMMARY);
      if (isInline) {
        setAssignSellRequestsOpen(false);
      } else {
        setLastStep(STEPS.PX_SUMMARY);
      }
    };

    // Persist the chosen Xero org onto the order whenever it's saved (Add to
    // cart / Save deposits / Save Payments / Mark as Paid) — not only on Go To
    // Invoice. Skipped once an invoice exists (the org is locked then; switching
    // goes through the void-and-recreate flow instead).
    const pinXeroOrgToOrder = async (orderObj) => {
      if (!orderObj?.id || !xeroConnectionId || orderObj.xero_invoice_number) {
        return;
      }
      try {
        await axios.put(`/api/orders/${orderObj.id}`, {
          xero_connection_id: xeroConnectionId,
        });
      } catch (e) {
        /* non-fatal — invoice generation re-sends it anyway */
      }
    };

    const createNewUnpaidOrder = async () => {
      setSubmitting(true);

      try {
        // Backend's OrderValidationService rejects listings that
        // aren't active when registering a (px-)order. Admin actions
        // like MARK AS PAID land here with listings still in
        // submitted/new/deactivated state — auto-activate first so
        // the flow proceeds. Safe: the listing is about to be locked
        // into an order anyway.
        await Promise.all(
          (listings ?? []).map((l) =>
            l?.id
              ? axios
                  .put(`/api/listings/${l.id}/active`, { active: true })
                  .catch((err) =>
                    console.error("[RC] failed to activate listing", l.id, err)
                  )
              : Promise.resolve()
          )
        );

        // Suppress the global 422 toast for the cart / px-order
        // create — the backend returns specific per-listing reasons
        // (e.g. "The listing is reserved", "The listing is inactive")
        // under errors.items.{listingId}, a nested shape the generic
        // interceptor's flat extractor collapses into "Please check
        // your input and try again." We surface the specific reason
        // ourselves in the catch below.
        const silentConfig = { metadata: { silent: true } };
        const newOrder = await (isPxOrder
          ? registerPxOrder({
              client_id: client.id,
              // Multi-main: every listing's id goes into the PxOrder
              // so the backend creates one OrderItem per listing and
              // reserves every linked stock. `listing_id` retained as
              // the first entry for older backends that only read the
              // singular key.
              listing_id: listings[0].id,
              listing_ids: listings.map((l) => l.id),
              order_id: order?.id,
              sell_requests: sellRequests.map((sr) => sr.id),
              requestConfig: silentConfig,
            })
          : addToCart({
              clientId: client.id,
              items: listings.map((listing) => ({
                listing_id: listing.id,
                quantity: 1,
              })),
              isRegistering: true,
              requestConfig: silentConfig,
            }));

        setOrder(newOrder);
        initCommisions(newOrder);

        // A zero-payment order can still carry an expert fee — the cost is
        // independent of what the client has paid.
        await saveExpertFee(newOrder?.id);

        await pinXeroOrgToOrder(newOrder);

        goToInvoice(newOrder);

        return newOrder;
      } catch (error) {
        // Surface the specific 422 reason from the cart/px-order
        // create. Laravel returns
        //   { errors: { items: { "<listing_id>": "<message>" } } }
        // — a nested object keyed by listing id, not the standard
        // array-per-field format. Walk one level deeper than the
        // generic interceptor so "The listing is reserved" / "The
        // listing is inactive" actually reach the rep.
        const itemErrors = error?.response?.data?.errors?.items;
        let message = null;
        if (itemErrors && typeof itemErrors === "object") {
          message =
            Object.values(itemErrors).find((v) => typeof v === "string") ??
            null;
        }
        if (!message) {
          message =
            error?.response?.data?.message ||
            "Failed to add to cart. Please try again.";
        }
        alert.show(message);
        return null;
      } finally {
        setSubmitting(false);
      }
    };

    // Transaction fee → pence for the wire. An empty fee stays null (what the
    // old parseInt("") * 100 = NaN serialised to), so a blank fee is not
    // newly saved as 0; anything typed keeps its pence (L11).
    const feeToPence = (fee) =>
      fee === "" || fee == null ? null : toPence(fee);

    const prepareOrderDataWithPayments = (state = null) => {
      let order_status = "";
      if (state === "paid") {
        order_status = "fully_paid";
      } else if (state === "deposit") {
        order_status = "not_fully_paid";
      }
      const data = {
        client_id: client.id,
        order_id: order?.id,
        listings: listings.map((listing) => listing.id),
        sell_requests: sellRequests.map((sr) => sr.id),
        fees: toPence(marketplaceFee),
        order_status,
        // STRIPE LINK rows are now sent through registerDeposits like any
        // other method: the backend validator accepts method=stripe_link,
        // PaymentService transforms it to method=stripe_card with the
        // __stripe_link__ sentinel and status=created (draft). When the
        // rep clicks Generate Link, PaymentLinkService::generate attaches
        // the Stripe intent + PaymentLink to that existing draft Payment
        // instead of creating a fresh one (no duplicates).
        deposits: Object.keys(deposits).reduce((acc, listingId) => {
          const listingDeposits = deposits[listingId]
            .filter((deposit) => deposit.amount > 0)
            .map((deposit) => ({
              payment_id: deposit.payment_id,
              listing_id: listingId,
              amount: toPence(deposit.amount),
              // A row with no currency is LEGACY GBP — the same rule
              // convertAmount() / paymentRows apply, so the figure is SAVED as
              // the currency it was already being summed as. The bare
              // `.toUpperCase()` also threw a TypeError on such a row, and
              // createDeposits' empty catch swallowed it: Save Deposits did
              // nothing at all, with no error shown.
              currency: (deposit.currency || "gbp").toUpperCase(),
              fee: feeToPence(deposit.fee),
              date: deposit.date,
              method: deposit.method,
              card_type: deposit.card_type,
              payment_status: deposit.payment_status,
              bank_account_id: deposit.bank_account_id,
            }));

          return [...acc, ...listingDeposits];
        }, []),
      };

      if (totalRemainingPaid.amount) {
        data.payment = {
          payment_id: totalRemainingPaid.payment_id,
          amount: toPence(totalRemainingPaid.amount),
          // Legacy-GBP fallback + crash guard, as for the deposit rows above.
          currency: (totalRemainingPaid.currency || "gbp").toUpperCase(),
          fee: feeToPence(totalRemainingPaid.fee),
          date: totalRemainingPaid.date,
          method: totalRemainingPaid.method,
          card_type: totalRemainingPaid.card_type,
          status: totalRemainingPaid.payment_status,
          bank_account_id: totalRemainingPaid.bank_account_id,
        };
      }

      return data;
    };

    // Split Commission payload — flatten the slot-shaped local state
    // into one entry per slot for the backend. Backend expects:
    //   commissions[].order_item_id, user_type, user_id, percentage,
    //   add_to_cost, payments[] (required only when add_to_cost ON).
    // Slots without a user are skipped (user is required by validation).
    const prepareCommissions = () => {
      const out = [];
      Object.keys(commissions).forEach((itemId) => {
        const slots = commissions[itemId]?.slots ?? [];
        slots.forEach((slot) => {
          if (!slot?.user?.id || !slot?.user_type) return;
          const itemEntry = order?.items?.find((i) => String(i.id) === String(itemId));
          const profitPence = itemEntry ? itemProfit(itemEntry) : 0;
          // profitPence is GBP (itemProfit). The rep's slot currency is what
          // the payout is recorded in, so convert the GBP share into that
          // currency — keeping the stored amount and its `currency` label in
          // agreement (previously a GBP value was stored under the slot's
          // currency). Flag-off ⇒ ÷1 and currency 'gbp' ⇒ byte-identical.
          const slotCurrency = slot.currency || "gbp";
          const amountGbpPence = ((Number(slot.percentage) || 0) / 100) * profitPence;
          const amountPence = gbpToCurrency(amountGbpPence, slotCurrency);
          const payments = slot.add_to_cost
            ? [
                {
                  payment_id: slot.payment_id,
                  amount: amountPence,
                  currency: slotCurrency.toUpperCase(),
                  date: slot.date || null,
                  method: slot.method || "offline",
                  card_type: slot.card_type || null,
                  fee: toPence(slot.fee),
                  payment_status: slot.payment_status || "created",
                  bank_account_id: slot.bank_account_id || null,
                },
              ]
            : [];
          out.push({
            order_item_id: itemId,
            user_type: slot.user_type,
            user_id: slot.user.id,
            percentage: Number(slot.percentage) || 0,
            add_to_cost: !!slot.add_to_cost,
            payments,
          });
        });
      });
      return out.length ? out : null;
    };

    const activateListingsForOrder = () =>
      Promise.all(
        (listings ?? []).map((l) =>
          l?.id
            ? axios
                .put(`/api/listings/${l.id}/active`, { active: true })
                .catch((err) =>
                  console.error("[RC] failed to activate listing", l.id, err)
                )
            : Promise.resolve()
        )
      );

    const createPaidOrder = async () => {
      setSubmitting(true);

      try {
        // Same auto-activation as createNewUnpaidOrder — backend's
        // OrderValidationService rejects deposits/payments against
        // non-active listings.
        await activateListingsForOrder();

        const order = await registerPaidOrder(
          prepareOrderDataWithPayments("paid")
        );
        droppedSavedDepositRef.current = false;

        setOrder(order);
        initCommisions(order);

        hydrateClientDepositsAfterSave(order);

        // After the order exists and BEFORE goToInvoice: the expert fee is a
        // separate record that must not be lost if the rep leaves at the
        // invoice step. It cannot affect the invoice — see saveExpertFee.
        await saveExpertFee(order?.id);

        await pinXeroOrgToOrder(order);

        if (!hasPendingStripeLinkRow()) {
          goToInvoice(order);
        }
      } catch (error) {
        showApiError(error, "finish the order");
      } finally {
        setSubmitting(false);
      }
    };

    const createDeposits = async () => {
      setSubmitting(true);

      try {
        await activateListingsForOrder();

        const order = await registerDeposits(
          prepareOrderDataWithPayments("deposit")
        );
        // The server now matches the form, removed deposits included.
        droppedSavedDepositRef.current = false;

        setOrder(order);
        initCommisions(order);

        hydrateClientDepositsAfterSave(order);

        // See createPaidOrder — saved alongside the payments, never with them.
        await saveExpertFee(order?.id);

        await pinXeroOrgToOrder(order);

        if (!hasPendingStripeLinkRow()) {
          goToInvoice(order);
        }
      } catch (error) {
        showApiError(error, "finish the order");
      } finally {
        setSubmitting(false);
      }
    };

    // After register-deposits / register-paid-order returns, write the
    // OrderItem id back to every client-state deposit row (so STRIPE LINK
    // rows can show Generate Link — their canGenerate gate needs it) and
    // write payment_id back from the corresponding server row. Server now
    // returns all rows including stripe_link drafts (Path A), so indices
    // align 1:1 with the client state. For TOTAL REMAINING PAID, hydrate
    // payment_id from order.payment.id so its Generate Link button works
    // identically to deposit rows.
    const hydrateClientDepositsAfterSave = (savedOrder) => {
      savedOrder?.items?.forEach((item) => {
        const clientRows = deposits[item.listing_id] || [];

        clientRows.forEach((_, idx) => {
          updateDepositField(item.listing_id, idx, "order_item_id", item.id);
        });

        item?.deposits?.forEach((serverDep, idx) => {
          updateDepositField(
            item.listing_id,
            idx,
            "payment_id",
            serverDep.payment_id
          );
        });
      });

      // TRP path: register-paid-order returns the new TRP Payment as
      // savedOrder.payment. Write its id back so the TRP row's Generate
      // Link button (canGenerate gates on order.id and the modal forwards
      // inlinePaymentId so PaymentLinkService::generate attaches the link
      // to the draft instead of minting a duplicate).
      if (savedOrder?.payment?.id) {
        setTotalRemainingPaid((prev) => ({
          ...prev,
          payment_id: savedOrder.payment.id,
          payment_status: savedOrder.payment.status ?? prev.payment_status,
        }));
      }
    };

    // A STRIPE LINK row is "pending" when its draft Payment exists
    // (payment_id is set after save) but no PaymentLink has been generated
    // yet. The save flow stays on the PAYMENTS step in that state so the
    // rep can click Generate Link — applies to deposit rows AND the TRP
    // row.
    const hasPendingStripeLinkRow = () => {
      const inDeposits = Object.values(deposits)
        .flat()
        .some((d) => d.method === "stripe_link" && !d.payment_link_id);
      const inTrp =
        totalRemainingPaid?.method === "stripe_link" &&
        !totalRemainingPaid?.payment_link_id;
      return inDeposits || inTrp;
    };

    const updateListingTaxRate = async (id, taxRate) => {
      if (!taxRate) {
        return;
      }

      setSubmitting(true);

      try {
        await axios.put(`/api/listings/${id}/tax-rate`, {
          tax_rate: taxRate,
        });
      } catch (error) {
        showApiError(error, "save the tax rate");
      } finally {
        setSubmitting(false);
      }
    };

    // Persist Split Commission slots to the backend then advance. The
    // backend's /register-commissions endpoint now accepts
    // percentage + add_to_cost per slot and skips Payment creation when
    // add_to_cost is OFF. If nothing meaningful to save (no slot has a
    // user), still advance — the rep may want to defer.
    // COMMISSIONS is now the LAST section before TRACKING NUMBER: costs were
    // taken before payments, so sending the rep back to them after the invoice
    // is finished (which is what `goToCosts()` did here) would walk the flow
    // backwards. Alex, 2026-09-04: "after INVOICE it goes to the next section
    // present naturally, instead of INTERNAL DETAILS & COSTS".
    const saveCommissions = async () => {
      // The payout amounts are converted figures: never save a guessed one.
      if (fxMissing) {
        alert.show(FX_SAVE_BLOCKED);
        return;
      }
      const payload = prepareCommissions();
      if (!payload) {
        return goToTrackingNumber();
      }
      setSubmitting(true);
      try {
        await registerCommissions({ commissions: payload });
        goToTrackingNumber();
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.errors ||
          "Failed to save commissions.";
        alert.show(typeof msg === "string" ? msg : "Failed to save commissions.");
      } finally {
        setSubmitting(false);
      }
    };

    const updateListingPrice = async () => {
      // newListingPrice is a converted total: never write a guessed one.
      if (fxMissing) {
        alert.show(FX_SAVE_BLOCKED);
        return;
      }
      setSubmitting(true);

      try {
        // `newListingPrice` covers the WHOLE order, so it may only be written
        // when the order has exactly one listing to carry it — which is what
        // gates the dialog's button (Payments.jsx). Re-check it here rather
        // than trusting listings[0]: if that gate ever changes, blindly
        // targeting index 0 would stamp the whole order's total onto one of
        // several listings. Multi-main is handled by preserving every other
        // listing in state (the previous return `[newListing]` clobbered the
        // array down to one entry, silently dropping listings 2 / 3 from the
        // PAYMENTS section and downstream order creation).
        const target = (listings ?? []).length === 1 ? listings[0] : null;
        if (!target?.id) return;
        await axios.put(`/api/listings/${target.id}/price`, {
          price: newListingPrice,
          // Lower-case, as the backend stores it (ListingController::updatePrice
          // normalises either case) and as `listingRate` already resolves it.
          currency: listingRate,
        });
        // Optimistic state must match the payload on BOTH fields, or the
        // LISTING heading (which reads listing.currency) keeps disagreeing with
        // the dialog until a reload.
        setListings((prev) =>
          (prev ?? []).map((l) =>
            String(l?.id) === String(target.id)
              ? { ...l, price: newListingPrice, currency: listingRate }
              : l
          )
        );
        await onRequestRefresh?.();
      } catch (error) {
        showApiError(error, "save the listing price");
      } finally {
        setSubmitting(false);
      }
    };

    const showPaymentDialog = () => {
      setPaymentDialog({
        open: true,
        step: PAYMENT_DIALOG.STEPS.QUESTION,
        decision: null,
      });
    };

    const updatePaymentDialog = (decision) => {
      switch (decision) {
        case PAYMENT_DIALOG.DECISIONS.CANNOT_DECIDE:
          return setPaymentDialog({
            ...paymentDialog,
            open: false,
          });
        case PAYMENT_DIALOG.DECISIONS.UPDATE_LISTING_PRICE:
          return setPaymentDialog({
            ...paymentDialog,
            decision,
            step: PAYMENT_DIALOG.STEPS.CONFIRMATiON,
          });
        case PAYMENT_DIALOG.DECISIONS.CONFIRM:
          setPaymentDialog({
            ...paymentDialog,
            open: false,
          });

          return updateListingPrice();
        default:
          return setPaymentDialog({
            ...paymentDialog,
            decision: null,
            step: PAYMENT_DIALOG.STEPS.QUESTION,
          });
      }
    };

    const savePayments = async () => {
      if (!arePaymentsValid) {
        return setShowPaymentErrors(true);
      }

      // The routing below (paid / deposits / unpaid) compares converted totals,
      // so with a guessed rate it could mark an order paid.
      if (fxMissing) {
        alert.show(FX_SAVE_BLOCKED);
        return;
      }

      // STRIPE LINK rows (deposits + TRP) now route through the normal
      // save flow. Backend stores them as draft Payments and
      // updateOrderStatus uses Payment.status — a draft sits at
      // status=created, so updateOrderStatus naturally lands the Order on
      // STATUS_CREATED rather than paid. Webhook completion via markPaid
      // then flips the Order to PAID when totalPaid catches up.
      //
      // Route on populatedAmount (includes drafts) not paidAmount
      // (excludes drafts): a stripe_link-only row is still a row the
      // rep wants persisted, so it needs createDeposits / createPaidOrder
      // — not createNewUnpaidOrder, which would drop the draft entirely.
      // An existing order whose deposits have all been cleared (populated
      // amount now 0) must still route through register-deposits: that
      // endpoint's reconciliation is the ONLY path that deletes the
      // previously-saved Deposit/Payment rows. createNewUnpaidOrder only
      // re-runs add-to-cart and never sends the (now-empty) deposits array,
      // so without this guard the cleared deposits linger in the DB and
      // reappear on reload. A brand-new order with nothing ever saved has
      // no server-side deposits to delete, so it still falls through to the
      // add-to-cart path below.
      //
      // The rows alone are not enough: clearing a saved deposit's amount to 0
      // SPLICES its row whenever another row follows it (updateDepositField),
      // taking its payment_id with it. With no row left to say "something was
      // saved", the save fell through to add-to-cart, the deposit survived on
      // the server, and the regenerated invoice still showed it. The saved
      // order itself records which deposits exist, so ask it too.
      const hasExistingDeposits =
        Object.values(deposits).some((rows) =>
          (rows || []).some((row) => !!row.payment_id)
        ) ||
        droppedSavedDepositRef.current ||
        (order?.items || []).some((item) => (item?.deposits || []).length > 0);

      switch (true) {
        case populatedAmount === 0 && !!order?.id && hasExistingDeposits:
          createDeposits();
          break;
        case populatedAmount === 0:
          createNewUnpaidOrder();
          break;
        case populatedAmount === toPay:
          createPaidOrder();
          break;
        case populatedAmount !== toPay && !totalRemainingPaid.amount:
          createDeposits();
          break;
        default:
          showPaymentDialog();
      }
    };

    // Returns a human-readable list of rows that lack a real bank account.
    // XeroService reads each Payment's bank_account_id and sends it as the
    // Xero AccountID — a missing value or the STRIPE LINK sentinel would
    // cause Xero to 400. Block Go To Invoice until the rep fills these in.
    // STRIPE LINK rows that aren't yet paid (status !== 'completed') are
    // skipped — they shouldn't be on an invoice yet anyway.
    const paymentsMissingBankAccount = () => {
      const missing = [];
      const isMissing = (bankAccountId) =>
        !bankAccountId || bankAccountId === "__stripe_link__";

      Object.entries(deposits).forEach(([listingId, rows]) => {
        rows.forEach((row, idx) => {
          if (!row?.amount || parseFloat(row.amount) <= 0) return;
          if (row.payment_status && row.payment_status !== "completed") return;
          if (isMissing(row.bank_account_id)) {
            missing.push(`Deposit ${idx + 1} (listing ${listingId})`);
          }
        });
      });

      if (
        totalRemainingPaid?.amount &&
        parseFloat(totalRemainingPaid.amount) > 0 &&
        (!totalRemainingPaid.payment_status ||
          totalRemainingPaid.payment_status === "completed") &&
        isMissing(totalRemainingPaid.bank_account_id)
      ) {
        missing.push("Total Remaining Paid");
      }

      return missing;
    };

    const goToInvoice = async (itemOrder = null) => {
      try {
        // Xero blocks any payment with a missing or sentinel bank account
        // (the STRIPE LINK draft sentinel persists on the Payment even
        // after the customer pays — markPaid doesn't overwrite it). Make
        // the rep pick a real bank account before we attempt the invoice
        // so the failure surfaces in the UI, not as an opaque Xero error.
        const missing = nativeInvoice ? [] : paymentsMissingBankAccount();
        if (missing.length > 0) {
          alert.show(
            "Select a bank account for the following payment(s) before generating the invoice: " +
              missing.join(", ")
          );
          return;
        }

        await updateMarketPlaceFee(itemOrder);
        // Persist the EXPERT FEE here too, for the same reason the marketplace
        // fee is: a rep who edits only that row and clicks GO TO INVOICE never
        // passes through savePayments, and would silently lose it. It is still
        // its own request against its own endpoint — the go-to-invoice payload
        // below is untouched, so the fee cannot reach the invoice.
        await saveExpertFee((itemOrder ?? order)?.id);
        const targetOrder = itemOrder ?? order;
        const getPdf = await axios.post("/api/order-management/go-to-invoice", {
          order: targetOrder,
          quotes,
          // Which Xero org to invoice into — the current dropdown selection wins
          // (so a just-switched org is honoured; local order may be stale). The
          // backend ignores this when the order already has an invoice (it stays
          // in its original org unless explicitly switched via switch-xero-org).
          xero_connection_id: xeroConnectionId ?? targetOrder?.xero_connection_id,
        });

        // A 5xx resolves undefined (global interceptor, which has already
        // told the rep). Stay on this step — moving to INVOICE with no PDF
        // read as "invoice created" when it was not.
        if (!getPdf) {
          return;
        }

        setGetPdf(getPdf?.data?.fileId);
        setGetPdfUrl(getPdf?.data?.fileUrl);
        setStep(STEPS.INVOICE);
        setLastStep(STEPS.INVOICE);
      } catch (error) {
        // serverMessage: the invoice step answers with reasons a merchant acts
        // on — an unmapped Xero account or tax code — which the status alone
        // cannot express. Sanitised, so an exception still never gets through.
        showApiError(error, "create the invoice", { serverMessage: true });
      }
    };

    // Changing the Xero org while an invoice already exists isn't a silent
    // re-pin: it voids the current invoice (and its recorded Xero payments) in
    // the old org and re-creates everything in the newly selected org. Confirmed
    // with the admin first. Covers both order and px-order (same flow).
    const onXeroOrgChange = async (newId) => {
      const prev = order?.xero_connection_id ?? xeroConnectionId;
      const previousBrandingThemeId = xeroBrandingThemeId;
      setXeroConnectionId(newId); // optimistic — keeps the dropdown in sync

      // Themes belong to an ORGANISATION — Xero rejects a BrandingThemeID from
      // another one outright — so a change of org drops the pick and falls back
      // to the new org's default. Done here, on the user's action, rather than
      // in an effect watching the org: an effect also fires while an order is
      // LOADING (the auto-defaulted org being replaced by the order's pinned
      // one), which would wipe the saved theme just for opening the order.
      if (Number(newId) !== Number(prev)) {
        setXeroBrandingThemeId(null);
        pendingBrandingThemeRef.current = null;
      }

      if (!order?.xero_invoice_number || !newId || Number(newId) === Number(prev)) {
        return;
      }

      // Hand the decision to SwitchXeroOrgModal (see below) instead of
      // window.confirm. Everything needed to describe the switch — and to snap
      // the dropdown back if it's cancelled or fails — is captured here.
      setXeroOrgSwitch({
        orderId: order.id,
        fromId: prev ?? null,
        toId: newId,
        invoiceNumber: order.xero_invoice_number,
        previousBrandingThemeId: previousBrandingThemeId ?? null,
        submitting: false,
        error: null,
        missingInvoice: null,
      });
    };

    // Restore the picker to where it was before an abandoned/failed switch. The
    // org is only ever really changed by the server, so the optimistic
    // selection must not survive a switch that didn't happen.
    const revertXeroOrgSelection = (pending) => {
      setXeroConnectionId(pending?.fromId ?? null);
      setXeroBrandingThemeId(pending?.previousBrandingThemeId ?? null);
    };

    const cancelXeroOrgSwitch = () => {
      if (xeroOrgSwitch?.submitting) return;
      revertXeroOrgSelection(xeroOrgSwitch);
      setXeroOrgSwitch(null);
    };

    const confirmXeroOrgSwitch = async ({ clearMissingInvoice = false } = {}) => {
      const pending = xeroOrgSwitch;
      if (!pending || pending.submitting) return;

      setXeroOrgSwitch({ ...pending, submitting: true, error: null });

      try {
        await axios.post(
          `/api/order-management/${pending.orderId}/switch-xero-org`,
          {
            xero_connection_id: pending.toId,
            // Only ever sent after the rep ticked the second confirmation in
            // the modal — never inferred from the 409 itself.
            ...(clearMissingInvoice ? { clear_missing_invoice: true } : {}),
          },
          // The modal owns ALL messaging for this action, so opt out of the
          // global interceptor's toast — otherwise the reason appears twice, in
          // two places, one of which is easy to miss.
          { metadata: { silent: true } }
        );
      } catch (e) {
        const data = e?.response?.data;

        // 409 + invoice_missing: answerable, so keep the dialog open and let the
        // rep assert it. Previously a dead end — the UI had no way to send
        // clear_missing_invoice, so the order could never be moved at all.
        if (data?.invoice_missing) {
          setXeroOrgSwitch({
            ...pending,
            submitting: false,
            error: data?.message ?? null,
            missingInvoice: {
              invoice_number: data.invoice_number,
              organisation: data.organisation,
            },
          });
          return;
        }

        setXeroOrgSwitch({
          ...pending,
          submitting: false,
          // Xero's real reason (payments still attached, un-reconcile first,
          // permissions) — actionable, so show it in place rather than as a
          // toast that outlives the dialog.
          error: data?.message || "Couldn't switch Xero organisation.",
        });
        return;
      }

      setXeroOrgSwitch(null);

      // The old invoice is voided + unlinked server-side; go-to-invoice now
      // re-creates the invoice and re-records payments in the new org.
      await goToInvoice();

      // Re-read the order so the INVOICE section shows the NEW number. Without
      // this the local order object still carried the voided invoice number and
      // its old org, so a switch that fully succeeded still read as "nothing
      // happened" — the switch was blamed for a stale-state bug.
      try {
        const { data: updatedOrder } = await axios.get(
          `/api/orders/${pending.orderId}`
        );
        setOrder(updatedOrder);
        setXeroConnectionId(updatedOrder?.xero_connection_id ?? pending.toId);
        setXeroBrandingThemeId(updatedOrder?.xero_branding_theme_id ?? null);
        await onRequestRefresh?.();
      } catch (e) {
        /* non-fatal — the switch itself already committed */
      }
    };

    const goToCommissions = () => {
      setStep(STEPS.COMMISSIONS);
      setLastStep(STEPS.COMMISSIONS);
    };

    // `goToCosts()` is gone with its one caller (saveCommissions). Costs are
    // taken BEFORE payments now, so nothing in the flow jumps forward into that
    // section — leaving the helper exported would only invite the old wiring
    // back. The rep still reaches it by clicking the step, like any other.

    const saveListing = async (submitForm) => {
      try {
        const newListing = await submitForm();

        setListings((listings) => {
          const newListingIndex = listings.findIndex(
            (listing) => listing.id === newListing?.id
          );

          const newListings = [...listings];
          newListings[newListingIndex] = newListing;

          return newListings;
        });
      } catch (error) {
        console.log("Error saving listing", error);
      }
    };

    const updateOrder = async (isFinalized) => {
      if (!order) {
        return;
      }

      try {
        await axios.put("/api/orders/" + order.id, {
          costs_finalised: isFinalized ? 1 : 0,
        });
      } catch (error) {
        showApiError(error, "save the finalised state of these costs");
      }
    };

    /**
     * Persist (or clear) the order's EXPERT FEE.
     *
     * Its own request, deliberately NOT part of prepareOrderDataWithPayments:
     * that payload is what register-deposits / register-paid-order turn into
     * the Xero invoice's deposits and final payment. Sending the expert fee
     * with it is exactly how it would end up on a client's invoice. Keeping it
     * on a separate endpoint makes that impossible by construction.
     *
     * Failures are logged, not surfaced: the fee is optional and never blocks
     * saving the payments the invoice actually needs.
     */
    const saveExpertFee = async (orderId = null) => {
      const targetId = orderId ?? order?.id;
      if (!targetId) return;

      // Nothing entered and nothing previously saved — no call to make. An
      // emptied row that HAS been saved must still be sent: a blank amount is
      // how the backend is told to delete the record, so `payment_id` counts as
      // "previously saved" too (an order loaded before the row carried an `id`
      // would otherwise skip the clear and leave the fee stuck).
      const amount = parseFloat(expertFee?.amount || 0);
      if (
        !amount &&
        !expertFee?.expert_account_id &&
        !expertFee?.id &&
        !expertFee?.payment_id
      )
        return;

      try {
        const { data } = await axios.post(
          `/api/order-management/${targetId}/expert-fee`,
          {
            expert_account_id: expertFee.expert_account_id ?? null,
            payment_id: expertFee.payment_id ?? null,
            amount: toPence(amount),
            currency: (expertFee.currency || "gbp").toUpperCase(),
            date: expertFee.date || null,
            method: expertFee.method || "offline",
            card_type: expertFee.card_type || null,
            payment_status: expertFee.payment_status || "created",
          }
        );

        // Write the saved ids back so a second save in the same flow UPDATES
        // this fee rather than creating another. (The backend also falls back
        // to the fee's existing payment_id, so a dropped response can't orphan
        // a Payment — this just keeps client state honest.) A null response is
        // the cleared case: reset to an empty row.
        setExpertFee((prev) => ({
          ...prev,
          id: data?.id ?? null,
          payment_id: data?.payment_id ?? null,
        }));
      } catch (error) {
        console.error("[RC] failed to save expert fee", error);
      }
    };

    /**
     * Remove the order's EXPERT FEE outright — the explicit Remove action on
     * the row.
     *
     * Distinct from saving an emptied row: that path needs every field blanked
     * first, which a locked row (payment_status=completed) does not permit, and
     * nobody would guess it anyway. This deletes server-side and resets the row
     * to empty regardless of lock state.
     *
     * Unlike saveExpertFee, failures ARE surfaced: the rep asked for something
     * to be deleted, so silently leaving it in place would be worse than an
     * error. Local state is only cleared once the server confirms, so a failed
     * delete can't leave the UI claiming the fee is gone while it still counts
     * towards profit.
     */
    const removeExpertFee = async () => {
      const targetId = order?.id;

      // Never saved — nothing on the server to delete, just clear the form.
      if (!targetId || (!expertFee?.id && !expertFee?.payment_id)) {
        setExpertFee({
          amount: 0,
          currency: baseCurrency,
          method: "offline",
          expert_account_id: null,
        });
        return true;
      }

      try {
        await axios.delete(`/api/order-management/${targetId}/expert-fee`);
        setExpertFee({
          amount: 0,
          currency: baseCurrency,
          method: "offline",
          expert_account_id: null,
        });
        return true;
      } catch (error) {
        showApiError(error, "remove the expert fee");
        return false;
      }
    };

    const updateMarketPlaceFee = async (newOrder = null) => {
      const defaultOrder = newOrder ?? order;
      if (!defaultOrder) {
        return;
      }

      try {
        const { data } = await axios.put("/api/orders/" + defaultOrder.id, {
          marketplace_fee: toPence(marketplaceFee),
          marketplace_fee_type: marketplaceFeeType,
        });

        setOrder(data);

        return data;
      } catch (error) {
        showApiError(error, "save the marketplace fee");
      }
    };

    // Re-post the invoice so a value entered after it was raised (a serial, a
    // warranty date) reaches Xero. A failure here must NOT fail the save — the
    // listing is already written — but it must not be swallowed either: this
    // used to console.log and the rep saw a completely normal successful save
    // while the invoice still carried the old description.
    const regenerateInvoice = async () => {
      if (!order?.xero_invoice_number && !order?.invoice_number) {
        return;
      }

      try {
        await updateMarketPlaceFee();
        // requireResponse: a swallowed 5xx must reach the catch below, so the
        // rep is told the invoice was NOT updated — and the PDF already on
        // screen is not wiped to undefined.
        const getPdf = requireResponse(
          await axios.post("/api/order-management/go-to-invoice", {
            order: order,
            quotes,
          })
        );
        setGetPdf(getPdf?.data?.fileId);
        setGetPdfUrl(getPdf?.data?.fileUrl);
      } catch (error) {
        alert.show(invoiceNotUpdatedMessage(order?.xero_invoice_number || order?.invoice_number, error));
      }
    };

    const saveCosts = async (submitForm) => {
      setSubmitting(true);

      await saveListing(submitForm);

      await regenerateInvoice();

      // Pull the freshly-saved listing back into the V2 page so the
      // RequestSmartCard's Buying Cost / Other Costs / Net Profit /
      // Marketplace Fee rows re-derive from the new values. Without
      // this refresh the inline form updates its own state via
      // saveListing but enrichedState in RequestTemplateV2 keeps
      // showing the pre-save figures until a manual reload.
      await onRequestRefresh?.();

      if (costsCurrentListingIdx + 1 < listings.length) {
        setCostsCurrentListingIdx(costsCurrentListingIdx + 1);
      } else {
        // INTERNAL DETAILS & COSTS now precedes PAYMENTS, so finishing the last
        // listing's costs block hands over to PAYMENTS — not to TRACKING NUMBER,
        // which was the next section only while this one sat at the end.
        goToPayments();
      }

      setSubmitting(false);
    };

    const finaliseCosts = async (submitForm) => {
      setSubmitting(true);

      await saveListing(submitForm);
      // Early-returns when there is no Order yet — which is the normal case now
      // that this section comes BEFORE payments. `orders.costs_finalised` is
      // then simply not set, which is the truth: there is no Order to stamp.
      // Nothing is lost, because the fields themselves are on the listing.
      await updateOrder(true);

      // Also a no-op before an invoice exists (it checks
      // `order.xero_invoice_number`), so this is safe on the pre-payment pass
      // and still repairs the description on a later visit.
      await regenerateInvoice();

      await onRequestRefresh?.();

      goToPayments();

      setSubmitting(false);
    };

    const goToPayments = () => {
      setStep(STEPS.PAYMENTS);
      setLastStep(STEPS.PAYMENTS);
    };

    const goToTrackingNumber = () => {
      setStep(STEPS.TRACKING_NUMBER);
      setLastStep(STEPS.TRACKING_NUMBER);
    };

    const saveTrackingNumber = async (trackingNumber) => {
      if (!order) {
        return;
      }

      setSubmitting(true);

      try {
        await axios.put("/api/orders/" + order.id, {
          tracking_number: trackingNumber,
        });
      } catch (error) {
        showApiError(error, "save the tracking number");
      } finally {
        setSubmitting(false);
      }
    };

    return {
      step,
      setStep,
      lastStep,
      client,
      onToggleListing,
      selectedListings: listings.reduce((acc, listing) => {
        acc[listing.id] = true;
        return acc;
      }, {}),
      listings,
      sellRequests,
      pxLinkedSellRequests,
      order,
      createNewUnpaidOrder,
      isInline,
      assignSellRequestsOpen,
      setAssignSellRequestsOpen,
      sellRequestsSkipped,
      selectedSellRequests: sellRequests.reduce((acc, sr) => {
        acc[sr.id] = true;
        return acc;
      }, {}),
      quotes,
      setQuotes: handleSetQuotes,
      listingRate,
      // The currency a listing's OWN price is denominated in (NULL ⇒ legacy
      // GBP). Exposed so Payments labels each listing line with it instead of
      // the deal currency.
      listingPriceCurrency,
      // Order-currency → GBP rate, so GBP-pivot consumers (e.g. the smart
      // card's INTERNAL_DETAILS net profit) can convert order-currency
      // amounts (paidAmount, marketplaceFee) into GBP.
      sellingToGbpRate,
      // GBP → merchant base rate, so GBP-pivot figures (costs / commissions /
      // net profit) can be displayed in the store's own currency.
      gbpToBaseRate,
      // FX RULE: when true, the converted totals (paidAmount, toPay, fees,
      // commissions, profit...) were computed without a real rate and must not
      // be shown. fxUnavailable = the rate request failed (vs still loading).
      fxMissing,
      fxUnavailable,
      onSelectSellRequest,
      deposits,
      updateDepositField,
      removeDepositRow,
      taxRates,
      updateTaxRate,
      totalRemainingPaid,
      setTotalRemainingPaidField,
      expertFee,
      setExpertFeeField,
      removeExpertFee,
      skipSellRequests,
      proceedToPayment,
      goToSummary,
      error,
      clearError,
      submitting,
      onAssignListings,
      onRemoveSellRequest: sellRequests.length > 1 ? onRemoveSellRequest : null,
      savePayments,
      goToInvoice,
      xeroOrganisations,
      xeroConnectionId,
      setXeroConnectionId,
      onXeroOrgChange,
      // Drives SwitchXeroOrgModal — mount it wherever XeroOrgSelect is rendered.
      xeroOrgSwitch,
      confirmXeroOrgSwitch,
      cancelXeroOrgSwitch,
      xeroAddressId,
      onXeroAddressChange,
      xeroBrandingThemeId,
      onXeroBrandingChange,
      arePaymentsValid,
      showPaymentErrors,
      getPdf,
      getPdfUrl,
      // Bumped when a STRIPE LINK settles under the rep's feet. Payments adds
      // it to the GENERATED LINKS fetch deps so that table's status column
      // reflects the payment without a reload.
      linkSettleCount,
      // True when this order is invoiced as our own PDF (no Xero on the plan):
      // payment rows then carry no Xero bank account.
      nativeInvoice,
      paymentDialog,
      updatePaymentDialog,
      isPxOrder,
      paidAmount,
      // Collected rows in their own currencies — for surfaces that must LABEL
      // a paid total (paidAmount is pre-converted into listingRate).
      paymentRows,
      populatedAmount,
      sellRequestsAmount,
      // What UPDATE LISTING PRICE will write — displayed by the dialog and sent
      // by updateListingPrice, from this one value.
      newListingPrice,
      toPay,
      orderPaid: order?.status === "paid",
      items: order?.items || [],
      order_id: order?.id,
      initToggleStatus: order?.toggle_status,
      existUserCompany: !isEmpty(client?.company),
      goToCommissions,
      commissions,
      updateCommissionField,
      addCommissionSlot,
      removeCommissionSlot,
      itemProfit,
      saveCommissions,
      commissionsAmount,
      commissionFees,
      // Exposed so the Order Summary card can show the fee on its own row and
      // say that it is already inside the profit the Commissions figure splits.
      expertFeeGbp,
      netProfitBeforeCommissions,

      flowModalOpen,
      onClose: () => toggleFlowModal(false),
      costsCurrentListingIdx,
      fees,
      marketplaceFee,
      setMarketplaceFee,
      marketplaceFeeType,
      setMarketplaceFeeType,
      allPaymentsConfirmed,
      saveCosts,
      finaliseCosts,
      trackingNumber: trackingNumber ?? "",
      setTrackingNumber,
      saveTrackingNumber,
    };
  }
);

export { OrderManagementProvider, useOrderManagement };
